import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api, { knowledgeAPI, API_BASE_URL } from '../api';
import ConfirmDialog from './ConfirmDialog';

// "Does this connector actually answer?" — one read-only call, and a verdict
// the admin can act on. Saving only stores a URL; nothing else ever checks it,
// and the same handful of setup mistakes otherwise surface days later as a bad
// chat answer. Always tests the SAVED row, so an edited URL needs a Save first.
const ConnectionTest = ({ kind, id, state, onTest }) => (
  <div>
    <div className="flex items-center gap-2">
      <button
        type="button"
        disabled={!id || state?.busy}
        onClick={() => onTest(kind, id)}
        className="px-2 py-1 bg-gray-600 hover:bg-gray-500 disabled:opacity-40 text-white rounded text-xs"
        title={id ? 'Checks the saved URL and token' : 'Save this row first'}
      >
        {state?.busy ? 'Testing…' : 'Test connection'}
      </button>
      {!id && <span className="text-[11px] text-gray-500">save first</span>}
      {/* Apps Script cold starts really do take ~30s on the first call */}
      {state?.busy && <span className="text-[11px] text-gray-500">first call can take up to 45s</span>}
    </div>

    {state?.result && !state.busy && (
      <div
        className={`mt-1 text-[11px] rounded px-2 py-1 border ${
          state.result.ok
            ? 'text-green-300 bg-green-500/10 border-green-500/30'
            : 'text-red-300 bg-red-500/10 border-red-500/30'
        }`}
      >
        <div>
          {state.result.ok ? '✓ ' : '✕ '}{state.result.summary}
          {typeof state.result.ms === 'number' && ` · ${(state.result.ms / 1000).toFixed(1)}s`}
        </div>
        {state.result.hint && <div className="text-gray-400 mt-0.5">{state.result.hint}</div>}
      </div>
    )}
  </div>
);

const AdminPanel = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddUser, setShowAddUser] = useState(false);
  // Generic confirm-dialog state: { title, message, confirmLabel?, onConfirm }
  const [pendingConfirm, setPendingConfirm] = useState(null);
  const [showEditSettings, setShowEditSettings] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // New user form state
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user',
    isActive: true
  });

  // Settings form state
  const [webhooks, setWebhooks] = useState([]);

  // Per-user AI assignment (provider + display name + system prompt + MCP servers)
  const [userAI, setUserAI] = useState({ provider: '', model: '', displayName: '', systemPrompt: '', mcpServers: [], mcpMode: 'native', confirmWrites: true });
  // Per-user FMS / Google Sheet task sources (chatbot task queries)
  const [userFMS, setUserFMS] = useState({ fmsId: '', sources: [] });
  // Connectors (Google Docs today; gmail/calendar later use the same shape)
  const [userConnectors, setUserConnectors] = useState([]);
  // Per-user voice mode (mic dictation + spoken replies) on/off + voice choice
  const [userVoiceEnabled, setUserVoiceEnabled] = useState(true);
  // Monthly AI message limit (0 = unlimited) + current-month usage (read-only)
  const [userMsgLimit, setUserMsgLimit] = useState(0);
  const [userUsage, setUserUsage] = useState(null);
  const [userVoiceId, setUserVoiceId] = useState('');
  // Dictation (speech-to-text) language for this user's mic input
  const [userSttLang, setUserSttLang] = useState('en-US');
  const STT_LANG_OPTIONS = [
    { value: 'en-US', label: 'English (US)' },
    { value: 'en-IN', label: 'English (India)' },
    { value: 'en-GB', label: 'English (UK)' },
    { value: 'hi-IN', label: 'Hindi (हिन्दी)' },
    { value: 'or-IN', label: 'Odia (ଓଡ଼ିଆ)' },
    { value: 'bn-IN', label: 'Bengali (বাংলা)' },
    { value: 'ta-IN', label: 'Tamil (தமிழ்)' },
    { value: 'te-IN', label: 'Telugu (తెలుగు)' },
    { value: 'mr-IN', label: 'Marathi (मराठी)' },
    { value: 'gu-IN', label: 'Gujarati (ગુજરાતી)' },
    { value: 'kn-IN', label: 'Kannada (ಕನ್ನಡ)' },
    { value: 'ml-IN', label: 'Malayalam (മലയാളം)' },
    { value: 'pa-IN', label: 'Punjabi (ਪੰਜਾਬੀ)' },
    { value: 'es-ES', label: 'Spanish' },
    { value: 'fr-FR', label: 'French' },
    { value: 'de-DE', label: 'German' },
    { value: 'it-IT', label: 'Italian' },
    { value: 'pt-BR', label: 'Portuguese (Brazil)' },
    { value: 'ru-RU', label: 'Russian' },
    { value: 'ja-JP', label: 'Japanese' },
    { value: 'ko-KR', label: 'Korean' },
    { value: 'zh-CN', label: 'Chinese (Simplified)' },
    { value: 'zh-TW', label: 'Chinese (Traditional)' }
  ];

  // Analytics tab: usage dashboard (reads UsageRecord data on demand)
  const [tab, setTab] = useState('users');
  const [analytics, setAnalytics] = useState(null);
  const [analyticsMonth, setAnalyticsMonth] = useState(new Date().toISOString().slice(0, 7));
  // User Logs tab: pick a user, view their full chat, download it as CSV
  const [logUsers, setLogUsers] = useState([]);       // { id, name, email } for the dropdown
  const [logUserId, setLogUserId] = useState('');
  const [logData, setLogData] = useState(null);       // { user, conversations }
  const [logLoading, setLogLoading] = useState(false);
  const [logDownloading, setLogDownloading] = useState(false);
  // Billing: subscription plans + Razorpay credentials (org-wide)
  const [showBilling, setShowBilling] = useState(false);
  const [plansList, setPlansList] = useState([]);
  const [rzpStatus, setRzpStatus] = useState({ keyId: '', hasSecret: false, hasWebhookSecret: false });
  const [rzpForm, setRzpForm] = useState({ keyId: '', keySecret: '', webhookSecret: '', creditPriceINR: '' });
  const [newPlan, setNewPlan] = useState({ name: '', priceINR: '', monthlyMessages: '', dailyMessages: '', allowedProviders: [], mcpEnabled: true, connectorsEnabled: true, imagesEnabled: false, searchEnabled: false });
  const [billingMsg, setBillingMsg] = useState('');
  // API keys: let external systems (website, WhatsApp) chat as a chosen user
  const [showApiKeys, setShowApiKeys] = useState(false);
  const [apiKeysList, setApiKeysList] = useState([]);
  const [apiKeyUsers, setApiKeyUsers] = useState([]);      // dropdown of users a key can act as
  const [newApiKey, setNewApiKey] = useState({ name: '', userId: '', rateLimitPerMin: 20 });
  const [freshApiKey, setFreshApiKey] = useState('');       // shown once, right after creation
  const [apiKeysMsg, setApiKeysMsg] = useState('');
  // Per-user subscription state (settings modal)
  const [userBilling, setUserBilling] = useState({ planId: null, status: 'none', currentPeriodEnd: null });
  const [selPlanId, setSelPlanId] = useState('');
  const [payLink, setPayLink] = useState('');
  // Admin password reset (for users who forgot theirs)
  const [resetPw, setResetPw] = useState('');
  // Chat delete protection (admin-set password gate on history deletion)
  const [userDeleteProtected, setUserDeleteProtected] = useState(false);
  const [userDeletePw, setUserDeletePw] = useState('');
  const [userDeletePwClear, setUserDeletePwClear] = useState(false);
  const [resetMsg, setResetMsg] = useState('');

  // Per-user knowledge base (RAG)
  const [kbSources, setKbSources] = useState([]);
  const [kbText, setKbText] = useState('');
  const [kbSourceName, setKbSourceName] = useState('');
  const [kbUrl, setKbUrl] = useState('');
  const [kbBusy, setKbBusy] = useState(false);
  const [kbMessage, setKbMessage] = useState('');
  const kbFileRef = useRef(null);
  // Retrieval tester: a bad answer looks identical whether the chunk was never
  // found or the model ignored it. This shows which one it was.
  const [kbQuery, setKbQuery] = useState('');
  const [kbHits, setKbHits] = useState(null);

  // ElevenLabs premade voices that work on the free plan
  const VOICE_OPTIONS = [
    { id: '', label: 'Default (Sarah)' },
    { id: 'EXAVITQu4vr4xnSDxMaL', label: 'Sarah — mature, reassuring' },
    { id: 'CwhRBWXzGAHq8TQ4Fs17', label: 'Roger — laid-back, casual' },
    { id: 'JBFqnCBsd6RMkjVDRZzb', label: 'George — warm storyteller' },
    { id: 'FGY2WhTYpPnrIDTdsKH5', label: 'Laura — enthusiast, quirky' },
    { id: 'IKne3meq5aSn9XLyUdCD', label: 'Charlie — deep, confident' },
    { id: 'N2lVS1w4EtoT3dr4eOWO', label: 'Callum — husky' }
  ];

  // Shared org-wide MCP servers (managed in their own modal; also listed
  // as checkboxes in the per-user settings when provider is Claude)
  const [showMCP, setShowMCP] = useState(false);
  const [mcpList, setMcpList] = useState([]);
  const [mcpMessage, setMcpMessage] = useState('');
  const [orgMCPServers, setOrgMCPServers] = useState([]);

  // Shared connectors & task sheets — defined once here, assigned per user.
  // Saves configuring the same company sheet inside all 20 employees.
  const [showShared, setShowShared] = useState(false);
  const [sharedSheets, setSharedSheets] = useState([]);      // org FMS sources
  const [sharedConns, setSharedConns] = useState([]);        // org connectors
  const [connTests, setConnTests] = useState({});            // "kind:id" -> { busy, result }
  const [sharedMessage, setSharedMessage] = useState('');
  // What this user is assigned (loaded with their settings)
  const [userOrgSources, setUserOrgSources] = useState([]);
  const [userOrgConnectors, setUserOrgConnectors] = useState([]);
  // Per-user override of the plan's feature flags: inherit | on | off
  const [userFeatures, setUserFeatures] = useState({ mcp: 'inherit', connectors: 'inherit', images: 'inherit', search: 'inherit' });
  // Org-wide master switch for public self-serve signup
  const [signupEnabled, setSignupEnabled] = useState(false);
  const [signupSaving, setSignupSaving] = useState(false);
  // Outgoing mail — signup cannot work without it, so its state is shown
  // next to the signup switch rather than buried in a settings modal.
  const [mailConfigured, setMailConfigured] = useState(false);
  const [showMail, setShowMail] = useState(false);
  const [mailForm, setMailForm] = useState({ host: '', port: 587, secure: false, user: '', pass: '', from: '', appBaseUrl: '' });
  const [mailHasPass, setMailHasPass] = useState(false);
  const [mailTesting, setMailTesting] = useState(false);
  const [mailMsg, setMailMsg] = useState('');
  // Web search (org-wide key, chosen provider)
  const [searchForm, setSearchForm] = useState({ provider: 'brave', apiKey: '' });
  const [searchHasKey, setSearchHasKey] = useState(false);
  const [searchProviders, setSearchProviders] = useState([]);
  const [searchTesting, setSearchTesting] = useState(false);
  // Search boxes for assigning from a long shared list — with 200 shared
  // entries a plain checkbox list would be unusable, so only what this user
  // already has is shown and the rest is searched for.
  const [sheetSearch, setSheetSearch] = useState('');
  const [connSearch, setConnSearch] = useState('');
  const [mcpSearch, setMcpSearch] = useState('');

  // Assigned first (in the order the admin added them), then anything the
  // search matches — so the box the admin is looking at is always small.
  const splitAssigned = (all, assigned, search) => {
    const isOn = id => assigned.some(a => a.id === id);
    const q = search.trim().toLowerCase();
    const mine = all.filter(x => isOn(x.id));
    const rest = q
      ? all.filter(x => !isOn(x.id) &&
          (`${x.name} ${x.keywords || ''} ${x.type || ''}`).toLowerCase().includes(q))
      : [];
    return { mine, rest: rest.slice(0, 8), restTotal: all.length - mine.length };
  };

  // Shared org-wide AI API keys modal
  const [showAIKeys, setShowAIKeys] = useState(false);
  const [aiKeysStatus, setAiKeysStatus] = useState({});
  const [aiKeyInputs, setAiKeyInputs] = useState({ claude: '', openai: '', gemini: '', groq: '', deepseek: '', openrouter: '', elevenlabs: '' });
  const [aiKeysMessage, setAiKeysMessage] = useState('');

  const AI_PROVIDER_OPTIONS = [
    { value: '', label: 'Disabled' },
    { value: 'claude', label: 'Claude (Anthropic)' },
    { value: 'openai', label: 'ChatGPT (OpenAI)' },
    { value: 'gemini', label: 'Gemini (Google)' },
    { value: 'groq', label: 'Groq (fast, cheap)' },
    { value: 'deepseek', label: 'DeepSeek' },
    { value: 'openrouter', label: 'OpenRouter' },
    { value: 'ollama', label: 'Ollama (Local — no API key)' }
  ];

  const AI_MODEL_PLACEHOLDERS = {
    claude: 'claude-opus-4-8 (default)',
    openai: 'gpt-5.1 (default)',
    gemini: 'gemini-3.5-flash-lite (default)',
    ollama: 'qwen2.5:7b (default, from server .env)'
  };

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchUsers();
    }
    // fetchUsers reads currentPage/searchQuery from closure; those are the real deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, currentPage, searchQuery]);

  // Load analytics only when the tab is open — zero backend work otherwise
  useEffect(() => {
    if (tab !== 'analytics' || user?.role !== 'admin') return;
    setAnalytics(null);
    api.get(`/v2/admin/analytics?month=${analyticsMonth}`)
      .then(r => setAnalytics(r.data))
      .catch(() => setError('Failed to load analytics'));
  }, [tab, analyticsMonth, user]);

  // Load the User Logs dropdown only when that tab opens
  useEffect(() => {
    if (tab !== 'logs' || user?.role !== 'admin') return;
    api.get('/v2/admin/user-directory')
      .then(r => setLogUsers(r.data.users || []))
      .catch(() => setError('Failed to load user list'));
  }, [tab, user]);

  // Fetch the selected user's full chat history
  const loadUserLog = async (id) => {
    setLogUserId(id);
    setLogData(null);
    if (!id) return;
    try {
      setLogLoading(true);
      const r = await api.get(`/v2/admin/users/${id}/conversations`);
      setLogData(r.data);
    } catch {
      setError('Failed to load chat history');
    } finally {
      setLogLoading(false);
    }
  };

  // Download the selected user's chat as CSV. Uses the axios instance so the
  // auth token rides along — a plain <a href> would hit the route unauthenticated.
  const downloadUserLog = async () => {
    if (!logUserId) return;
    try {
      setLogDownloading(true);
      const r = await api.get(`/v2/admin/users/${logUserId}/conversations/export`, { responseType: 'blob' });
      const name = (logData?.user?.name || 'user').replace(/[^a-zA-Z0-9_-]/g, '_');
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `chat_${name}_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setError('Download failed');
    } finally {
      setLogDownloading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/v2/admin/users?query=${searchQuery}&page=${currentPage}&size=10`);
      setUsers(response.data.users);
      setTotalPages(response.data.pagination.pages);
    } catch (error) {
      setError('Failed to fetch users');
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const createUser = async (e) => {
    e.preventDefault();
    try {
      const response = await api.post('/v2/admin/users', newUser);
      if (response.data.success) {
        setShowAddUser(false);
        setNewUser({
          name: '',
          email: '',
          password: '',
          role: 'user',
          isActive: true
        });
        fetchUsers();
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to create user');
    }
  };

  // Load the signup switch once, alongside the user list.
  useEffect(() => {
    api.get('/v2/admin/org-settings/signup')
      .then(r => {
        setSignupEnabled(!!r.data.signupEnabled);
        setMailConfigured(!!r.data.mailConfigured);
      })
      .catch(() => {});
  }, []);

  const openMail = async () => {
    try {
      setMailMsg('');
      const r = await api.get('/v2/admin/org-settings/smtp');
      if (r.data.success) {
        setMailForm({ ...r.data.smtp, pass: '', appBaseUrl: r.data.appBaseUrl || '' });
        setMailHasPass(!!r.data.smtp.hasPass);
        setMailConfigured(!!r.data.configured);
      }
      setShowMail(true);
    } catch (error) {
      setError('Failed to load mail settings');
    }
  };

  const saveMail = async () => {
    try {
      const payload = { ...mailForm };
      // Blank means "keep the stored password", matching the Razorpay fields
      if (!String(payload.pass || '').trim()) delete payload.pass;
      const r = await api.put('/v2/admin/org-settings/smtp', payload);
      if (r.data.success) {
        setMailConfigured(!!r.data.configured);
        setMailHasPass(h => h || !!String(mailForm.pass || '').trim());
        setMailForm(f => ({ ...f, pass: '' }));
        setMailMsg('✅ Mail settings saved');
      }
    } catch (error) {
      setMailMsg('❌ ' + (error.response?.data?.message || 'Failed to save'));
    }
  };

  const testMail = async () => {
    setMailTesting(true);
    setMailMsg('');
    try {
      const r = await api.post('/v2/admin/org-settings/smtp/test');
      setMailMsg((r.data.ok ? '✅ ' : '❌ ') + r.data.message);
    } catch (error) {
      setMailMsg('❌ ' + (error.response?.data?.message || 'Test failed'));
    } finally {
      setMailTesting(false);
    }
  };

  const toggleSignup = async (next) => {
    setSignupSaving(true);
    // Optimistic, but reverted on failure — a switch that looks flipped while
    // the server still says otherwise is worse than a slow one.
    setSignupEnabled(next);
    try {
      const r = await api.put('/v2/admin/org-settings/signup', { signupEnabled: next });
      setSignupEnabled(!!r.data.signupEnabled);
    } catch (error) {
      setSignupEnabled(!next);
      setError(error.response?.data?.message || 'Failed to change signup setting');
    } finally {
      setSignupSaving(false);
    }
  };

  const updateUserSettings = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        webhooks: webhooks,
        aiProvider: userAI,
        fms: { ...userFMS, orgSources: userOrgSources },
        connectors: userConnectors,
        orgConnectors: userOrgConnectors,
        features: userFeatures,
        voiceEnabled: userVoiceEnabled,
        monthlyMessageLimit: Number(userMsgLimit) || 0,
        elevenLabsVoiceId: userVoiceId,
        sttLang: userSttLang,
      };

      // Delete-password: send only when the admin typed a new one or asked to
      // clear it — otherwise leave the stored hash untouched.
      if (userDeletePwClear) payload.chatDeletePassword = '';
      else if (userDeletePw.trim()) payload.chatDeletePassword = userDeletePw.trim();

      const response = await api.put(
        `/v2/admin/users/${selectedUser._id}/settings`,
        payload
      );
      if (response.data.success) {
        setShowEditSettings(false);
        setSelectedUser(null);
        setWebhooks([]);
        setUserAI({ provider: '', model: '', displayName: '', systemPrompt: '', mcpServers: [], mcpMode: 'native', confirmWrites: true });
        setUserFMS({ fmsId: '', sources: [] });
        setUserConnectors([]);
        setUserOrgConnectors([]);
        setUserFeatures({ mcp: 'inherit', connectors: 'inherit', images: 'inherit', search: 'inherit' });
        setUserOrgSources([]);
        setUserDeletePw('');
        setUserDeletePwClear(false);
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to update settings');
    }
  };

  // ---- Shared AI API keys (org-wide) ----

  // ---- API keys (external systems chat as a chosen user) ----

  const openApiKeys = async () => {
    setApiKeysMsg('');
    setFreshApiKey('');
    setNewApiKey({ name: '', userId: '', rateLimitPerMin: 20 });
    try {
      const [keysR, dirR] = await Promise.all([
        api.get('/v2/admin/api-keys'),
        api.get('/v2/admin/user-directory')
      ]);
      if (keysR.data.success) setApiKeysList(keysR.data.keys);
      if (dirR.data.success) setApiKeyUsers(dirR.data.users);
    } catch { setApiKeysMsg('Failed to load API keys'); }
    setShowApiKeys(true);
  };

  const createApiKey = async (e) => {
    e.preventDefault();
    setApiKeysMsg('');
    setFreshApiKey('');
    try {
      const r = await api.post('/v2/admin/api-keys', {
        name: newApiKey.name,
        userId: newApiKey.userId,
        rateLimitPerMin: Number(newApiKey.rateLimitPerMin) || 20
      });
      if (r.data.success) {
        setFreshApiKey(r.data.fullKey);   // shown once
        setNewApiKey({ name: '', userId: '', rateLimitPerMin: 20 });
        const list = await api.get('/v2/admin/api-keys');
        if (list.data.success) setApiKeysList(list.data.keys);
      }
    } catch (error) {
      setApiKeysMsg(error.response?.data?.message || 'Failed to create key');
    }
  };

  const toggleApiKey = async (k) => {
    try {
      await api.put(`/v2/admin/api-keys/${k.id}`, { isActive: !k.isActive });
      setApiKeysList(prev => prev.map(x => x.id === k.id ? { ...x, isActive: !x.isActive } : x));
    } catch { setApiKeysMsg('Failed to update key'); }
  };

  const deleteApiKey = async (k) => {
    if (!window.confirm(`Delete API key "${k.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/v2/admin/api-keys/${k.id}`);
      setApiKeysList(prev => prev.filter(x => x.id !== k.id));
    } catch { setApiKeysMsg('Failed to delete key'); }
  };

  // ---- Billing: plans + Razorpay credentials ----

  const fetchPlans = async () => {
    try {
      const r = await api.get('/billing/plans');
      if (r.data.success) setPlansList(r.data.plans);
    } catch (e) { /* plans are optional until billing is used */ }
  };

  const openBilling = async () => {
    try {
      setBillingMsg('');
      setRzpForm({ keyId: '', keySecret: '', webhookSecret: '', creditPriceINR: '' });
      const r = await api.get('/billing/razorpay');
      if (r.data.success) {
        setRzpStatus(r.data);
        setRzpForm(f => ({
          ...f,
          keyId: r.data.keyId || '',
          // Stored in paise; the admin thinks in rupees
          creditPriceINR: r.data.creditPricePaise ? String(r.data.creditPricePaise / 100) : ''
        }));
      }
      // Search settings live in the same modal, so load them together
      try {
        const s = await api.get('/v2/admin/org-settings/search');
        if (s.data.success) {
          setSearchForm({ provider: s.data.provider || 'brave', apiKey: '' });
          setSearchHasKey(!!s.data.hasKey);
          setSearchProviders(s.data.providers || []);
        }
      } catch { /* a missing search config must not block the billing modal */ }

      await fetchPlans();
      setShowBilling(true);
    } catch (error) {
      setError('Failed to load billing settings');
    }
  };

  const saveSearch = async () => {
    try {
      const payload = { provider: searchForm.provider };
      // Blank means "keep the stored key", matching the other secret fields
      if (searchForm.apiKey.trim()) payload.apiKey = searchForm.apiKey.trim();
      const r = await api.put('/v2/admin/org-settings/search', payload);
      if (r.data.success) {
        setSearchHasKey(!!r.data.hasKey);
        setSearchForm(f => ({ ...f, apiKey: '' }));
        setBillingMsg('✅ Search settings saved');
      }
    } catch (error) {
      setBillingMsg('❌ ' + (error.response?.data?.message || 'Failed to save search settings'));
    }
  };

  const testSearch = async () => {
    setSearchTesting(true);
    setBillingMsg('');
    try {
      const r = await api.post('/v2/admin/org-settings/search/test');
      setBillingMsg((r.data.ok ? '✅ ' : '❌ ') + r.data.message);
    } catch (error) {
      setBillingMsg('❌ ' + (error.response?.data?.message || 'Search test failed'));
    } finally {
      setSearchTesting(false);
    }
  };

  const saveRazorpay = async () => {
    try {
      // Only send fields the admin actually typed — untouched secrets stay as-is
      const payload = { keyId: rzpForm.keyId };
      if (rzpForm.keySecret.trim()) payload.keySecret = rzpForm.keySecret.trim();
      if (rzpForm.webhookSecret.trim()) payload.webhookSecret = rzpForm.webhookSecret.trim();
      // Blank means "leave it alone"; an explicit 0 means "turn top-ups off"
      if (String(rzpForm.creditPriceINR).trim() !== '') {
        payload.creditPricePaise = Math.round(Number(rzpForm.creditPriceINR) * 100);
      }
      const r = await api.put('/billing/razorpay', payload);
      if (r.data.success) {
        setBillingMsg('✅ Razorpay settings saved');
        setRzpForm(f => ({ ...f, keySecret: '', webhookSecret: '' }));
        const s = await api.get('/billing/razorpay');
        if (s.data.success) setRzpStatus(s.data);
      }
    } catch (error) {
      setBillingMsg('❌ ' + (error.response?.data?.message || 'Failed to save'));
    }
  };

  const addPlan = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post('/billing/plans', {
        name: newPlan.name,
        priceINR: Number(newPlan.priceINR),
        monthlyMessages: Number(newPlan.monthlyMessages),
        dailyMessages: Number(newPlan.dailyMessages) || 0,
        allowedProviders: newPlan.allowedProviders,
        mcpEnabled: newPlan.mcpEnabled,
        connectorsEnabled: newPlan.connectorsEnabled,
        imagesEnabled: newPlan.imagesEnabled,
        searchEnabled: newPlan.searchEnabled
      });
      if (r.data.success) {
        setNewPlan({ name: '', priceINR: '', monthlyMessages: '', dailyMessages: '', allowedProviders: [], mcpEnabled: true, connectorsEnabled: true, imagesEnabled: false, searchEnabled: false });
        setBillingMsg('✅ Plan added');
        fetchPlans();
      }
    } catch (error) {
      setBillingMsg('❌ ' + (error.response?.data?.message || 'Failed to add plan'));
    }
  };

  const togglePlan = async (plan) => {
    try {
      await api.put(`/billing/plans/${plan._id}`, { isActive: !plan.isActive });
      fetchPlans();
    } catch (error) {
      setBillingMsg('❌ Failed to update plan');
    }
  };

  // Flip one feature flag on an existing plan. Without this the flags could
  // only be chosen at creation time, so changing what a tier includes meant
  // deleting the plan and rebuilding it — taking every subscriber with it.
  const updatePlanFlag = async (plan, field, value) => {
    try {
      await api.put(`/billing/plans/${plan._id}`, { [field]: value });
      fetchPlans();
    } catch (error) {
      setBillingMsg('❌ ' + (error.response?.data?.message || 'Failed to update plan'));
    }
  };

  // ---- Per-user subscription actions (settings modal) ----

  const assignPlan = async (planId) => {
    try {
      const r = await api.post(`/billing/users/${selectedUser._id}/assign-plan`, { planId: planId || null });
      if (r.data.success) {
        setUserBilling(r.data.billing || { planId: null, status: 'none', currentPeriodEnd: null });
        setPayLink('');
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to assign plan');
    }
  };

  const resetUserPassword = async () => {
    if (!resetPw || resetPw.length < 6) {
      setResetMsg('❌ Password must be at least 6 characters');
      return;
    }
    try {
      await api.patch(`/v2/admin/users/${selectedUser._id}`, { resetPassword: resetPw });
      setResetMsg(`✅ Password reset — share the new password with ${selectedUser.name}`);
      setResetPw('');
    } catch (error) {
      setResetMsg('❌ ' + (error.response?.data?.message || 'Failed to reset password'));
    }
  };

  const createPayLink = async () => {
    try {
      setPayLink('...');
      const r = await api.post(`/billing/users/${selectedUser._id}/payment-link`, { planId: selPlanId });
      setPayLink(r.data.shortUrl || '');
    } catch (error) {
      setPayLink('');
      setError(error.response?.data?.message || 'Failed to create payment link');
    }
  };

  const openAIKeys = async () => {
    try {
      setAiKeysMessage('');
      setAiKeyInputs({ claude: '', openai: '', gemini: '', groq: '', deepseek: '', openrouter: '', elevenlabs: '' });
      const response = await api.get('/v2/admin/org-settings/ai-keys');
      if (response.data.success) {
        setAiKeysStatus(response.data.keys);
        setShowAIKeys(true);
      }
    } catch (error) {
      setError('Failed to load AI API keys');
    }
  };

  const saveAIKeys = async (e) => {
    e.preventDefault();
    try {
      setAiKeysMessage('');
      const payload = {};
      for (const provider of ['claude', 'openai', 'gemini', 'groq', 'deepseek', 'openrouter', 'elevenlabs']) {
        if ((aiKeyInputs[provider] || '').trim()) {
          payload[provider] = aiKeyInputs[provider].trim();
        }
      }
      const response = await api.put('/v2/admin/org-settings/ai-keys', payload);
      if (response.data.success) {
        setAiKeysStatus(response.data.keys);
        setAiKeyInputs({ claude: '', openai: '', gemini: '', groq: '', deepseek: '', openrouter: '', elevenlabs: '' });
        setAiKeysMessage(response.data.message || 'Saved');
      }
    } catch (error) {
      setAiKeysMessage(error.response?.data?.message || 'Failed to save keys');
    }
  };

  // ---- Shared MCP servers (org-wide) ----

  // ---- Shared connectors & task sheets ----
  const fetchShared = async () => {
    try {
      const response = await api.get('/v2/admin/org-settings/connectors');
      if (response.data.success) {
        setSharedConns((response.data.connectors || []).map(c => ({ ...c, token: '' })));
        setSharedSheets((response.data.fmsSources || []).map(s => ({ ...s, token: '' })));
      }
    } catch (error) {
      console.error('Failed to load shared connectors', error);
    }
  };

  const saveShared = async (e) => {
    e.preventDefault();
    try {
      setSharedMessage('');
      const strip = (o) => ({ ...o, ...(o.token?.trim() ? { token: o.token.trim() } : {}) });
      const response = await api.put('/v2/admin/org-settings/connectors', {
        connectors: sharedConns.map(strip),
        fmsSources: sharedSheets.map(strip)
      });
      if (response.data.success) {
        setSharedConns((response.data.connectors || []).map(c => ({ ...c, token: '' })));
        setSharedSheets((response.data.fmsSources || []).map(s => ({ ...s, token: '' })));
        setSharedMessage('✅ Saved');
      }
    } catch (error) {
      setSharedMessage(error.response?.data?.message || 'Failed to save');
    }
  };

  const addSharedSheet = () => setSharedSheets(prev => [...prev, {
    id: '', name: `Sheet-${prev.length + 1}`, keywords: '', url: '', sheetName: '',
    matchCol: 'A', showCols: 'A,B,C', statusCol: '', maxRows: 20, token: '', hasToken: false
  }]);
  const addSharedConn = () => setSharedConns(prev => [...prev, {
    id: '', type: 'docs', name: `Connector-${prev.length + 1}`, keywords: '', url: '', token: '', hasToken: false
  }]);
  const changeShared = (setter, i, field, value) =>
    setter(prev => prev.map((x, idx) => idx === i ? { ...x, [field]: value } : x));

  // Connection tests, keyed "kind:id" so one row's result never shows on another
  const runConnectionTest = async (kind, id) => {
    const key = `${kind}:${id}`;
    setConnTests(prev => ({ ...prev, [key]: { busy: true } }));
    try {
      const res = await api.post('/v2/admin/org-settings/test-connection', { kind, id });
      setConnTests(prev => ({ ...prev, [key]: { busy: false, result: res.data.result } }));
    } catch (err) {
      setConnTests(prev => ({
        ...prev,
        [key]: {
          busy: false,
          result: {
            ok: false,
            summary: err.response?.data?.message || 'Test failed',
            hint: ''
          }
        }
      }));
    }
  };
  const removeShared = (setter, i) => setter(prev => prev.filter((_, idx) => idx !== i));

  // Assignment toggles inside a user's settings
  const toggleAssign = (list, setter, id) => {
    const found = list.find(a => a.id === id);
    if (found) setter(list.filter(a => a.id !== id));
    else setter([...list, { id, viewMode: 'own', canRead: true, canWrite: false, canAdd: false, canEdit: false, canDelete: false }]);
  };
  const setAssignField = (list, setter, id, field, value) =>
    setter(list.map(a => a.id === id ? { ...a, [field]: value } : a));

  const fetchOrgMCPServers = async () => {
    try {
      const response = await api.get('/v2/admin/org-settings/mcp-servers');
      if (response.data.success) {
        setOrgMCPServers(response.data.servers);
        return response.data.servers;
      }
    } catch (error) {
      console.error('Failed to load MCP servers', error);
    }
    return [];
  };

  const openMCP = async () => {
    setMcpMessage('');
    const servers = await fetchOrgMCPServers();
    // token starts blank; blank = keep existing
    setMcpList(servers.map(s => ({ ...s, token: '' })));
    setShowMCP(true);
  };

  const saveMCP = async (e) => {
    e.preventDefault();
    try {
      setMcpMessage('');
      const payload = {
        servers: mcpList.map(s => ({
          id: s.id,
          name: s.name,
          url: s.url,
          ...(s.token.trim() ? { token: s.token.trim() } : {})
        }))
      };
      const response = await api.put('/v2/admin/org-settings/mcp-servers', payload);
      if (response.data.success) {
        setOrgMCPServers(response.data.servers);
        setMcpList(response.data.servers.map(s => ({ ...s, token: '' })));
        setMcpMessage('Saved!');
      }
    } catch (error) {
      setMcpMessage(error.response?.data?.message || 'Failed to save MCP servers');
    }
  };

  const addMCPRow = () => {
    setMcpList(prev => [...prev, {
      id: '',
      name: `MCP-${prev.length + 1}`,
      url: '',
      hasToken: false,
      token: ''
    }]);
  };

  const removeMCPRow = (index) => {
    setMcpList(prev => prev.filter((_, i) => i !== index));
  };

  const changeMCPRow = (index, field, value) => {
    setMcpList(prev => prev.map((s, i) => (i === index ? { ...s, [field]: value } : s)));
  };


  // ---- Knowledge base handlers (operate on the currently open user) ----
  const refreshKb = async () => {
    if (!selectedUser) return;
    try { setKbSources(await knowledgeAPI.list(selectedUser._id)); } catch { /* ignore */ }
  };

  const testKbQuery = async () => {
    if (!kbQuery.trim() || kbBusy) return;
    setKbBusy(true); setKbMessage('');
    try {
      setKbHits(await knowledgeAPI.search(selectedUser._id, kbQuery.trim()));
    } catch (err) {
      setKbHits(null);
      setKbMessage(err.response?.data?.message || 'Retrieval test failed');
    } finally {
      setKbBusy(false);
    }
  };

  const addKbText = async () => {
    if (!kbText.trim() || kbBusy) return;
    setKbBusy(true); setKbMessage('Processing…');
    try {
      const r = await knowledgeAPI.addText(selectedUser._id, kbSourceName.trim() || 'Manual text', kbText.trim());
      setKbMessage(r.message || 'Added');
      setKbText(''); setKbSourceName('');
      await refreshKb();
    } catch (err) {
      setKbMessage(err.response?.data?.message || 'Failed to add text');
    } finally { setKbBusy(false); }
  };

  const addKbUrl = async () => {
    if (!kbUrl.trim() || kbBusy) return;
    setKbBusy(true); setKbMessage('Fetching page…');
    try {
      const r = await knowledgeAPI.addUrl(selectedUser._id, kbUrl.trim());
      setKbMessage(r.message || 'Added');
      setKbUrl('');
      await refreshKb();
    } catch (err) {
      setKbMessage(err.response?.data?.message || 'Failed to fetch URL');
    } finally { setKbBusy(false); }
  };

  const uploadKbFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || kbBusy) return;
    setKbBusy(true); setKbMessage(`Processing ${file.name}…`);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await knowledgeAPI.uploadFile(selectedUser._id, fd);
      setKbMessage(r.message || 'Added');
      await refreshKb();
    } catch (err) {
      setKbMessage(err.response?.data?.message || 'Failed to process file');
    } finally { setKbBusy(false); }
  };

  const deleteKbSource = (source) => {
    setPendingConfirm({
      title: 'Remove this document?',
      message: `"${source}" will be removed from this user's knowledge base. The AI will no longer be able to answer from it.`,
      confirmLabel: 'Remove',
      onConfirm: async () => {
        setPendingConfirm(null);
        try {
          await knowledgeAPI.deleteSource(selectedUser._id, source);
          await refreshKb();
        } catch { setKbMessage('Failed to delete'); }
      }
    });
  };

  const toggleUserMCP = (serverId) => {
    setUserAI(prev => ({
      ...prev,
      mcpServers: prev.mcpServers.includes(serverId)
        ? prev.mcpServers.filter(id => id !== serverId)
        : [...prev.mcpServers, serverId]
    }));
  };

  // Per-user connectors/sheets are gone — they are defined once in
  // "Shared Connectors" (OrgSettings) and assigned to users above.

  const clearAIKey = (provider) => {
    setPendingConfirm({
      title: 'Clear this API key?',
      message: `Users assigned to ${provider} will lose AI chat access until a new key is added.`,
      confirmLabel: 'Clear key',
      onConfirm: async () => {
        setPendingConfirm(null);
        try {
          const response = await api.put('/v2/admin/org-settings/ai-keys', { [provider]: null });
          if (response.data.success) {
            setAiKeysStatus(response.data.keys);
            setAiKeysMessage(`${provider} key cleared`);
          }
        } catch (error) {
          setAiKeysMessage('Failed to clear key');
        }
      }
    });
  };

  const deactivateUser = (userId) => {
    setPendingConfirm({
      title: 'Deactivate this user?',
      message: 'They will immediately lose access and be signed out. You can reactivate them later.',
      confirmLabel: 'Deactivate',
      onConfirm: async () => {
        setPendingConfirm(null);
        try {
          await api.delete(`/v2/admin/users/${userId}`);
          fetchUsers();
        } catch (error) {
          setError('Failed to deactivate user');
        }
      }
    });
  };

  // Permanent delete needs the admin to retype the email, so it gets its own
  // modal rather than the yes/no ConfirmDialog used everywhere else.
  const [purgeTarget, setPurgeTarget] = useState(null);
  const [purgeTyped, setPurgeTyped] = useState('');
  const [purgeBusy, setPurgeBusy] = useState(false);
  const [purgeError, setPurgeError] = useState('');

  const openPurge = (user) => {
    setPurgeTarget(user);
    setPurgeTyped('');
    setPurgeError('');
  };

  const confirmPurge = async () => {
    if (!purgeTarget) return;
    setPurgeBusy(true);
    setPurgeError('');
    try {
      const r = await api.delete(`/v2/admin/users/${purgeTarget._id}/permanent`, {
        data: { confirmEmail: purgeTyped.trim() }
      });
      setPurgeTarget(null);
      fetchUsers();
      if (r.data?.message) setError(''); // clear any stale banner
    } catch (e) {
      setPurgeError(e.response?.data?.message || 'Could not delete this user.');
    } finally {
      setPurgeBusy(false);
    }
  };

  // The other half of deactivateUser. Its confirm dialog has always promised
  // "you can reactivate them later" — this is what finally makes that true.
  const reactivateUser = async (userId) => {
    try {
      // PATCH, not PUT — /v2/admin/users/:id is a partial update
      await api.patch(`/v2/admin/users/${userId}`, { isActive: true });
      fetchUsers();
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to reactivate user');
    }
  };

  const handleAddWebhook = () => {
    const newWebhook = {
      id: Date.now().toString(),
      name: `WebHook-${webhooks.length + 1}`,
      url: '',
      createdAt: new Date()
    };
    setWebhooks([...webhooks, newWebhook]);
  };

  const handleDeleteWebhook = (id) => {
    if (webhooks.length === 1) {
      alert('You must have at least one webhook');
      return;
    }
    setWebhooks(webhooks.filter(w => w.id !== id));
  };

  const handleWebhookChange = (id, field, value) => {
    setWebhooks(webhooks.map(w => 
      w.id === id ? { ...w, [field]: value } : w
    ));
  };

  const openEditSettings = async (user) => {
    setSelectedUser(user);
    try {
      const response = await api.get(`/v2/admin/users/${user._id}/settings`);
      if (response.data.success) {
        // Initialize webhooks array
        if (response.data.settings.webhooks && response.data.settings.webhooks.length > 0) {
          setWebhooks(response.data.settings.webhooks);
        } else {
          // Fallback to the old webhook1..webhook4 fields.
          //
          // A slot only counts as REAL if it has a URL, or a name the admin
          // actually changed. webhook2Name..webhook4Name carry schema defaults
          // ('WebHook-2' etc.), so testing the name alone was always true and
          // every user opened with four empty rows they never asked for.
          const s = response.data.settings;
          const defaultWebhooks = [1, 2, 3, 4]
            .map(n => ({
              id: String(n),
              name: s[`webhook${n}Name`] || `WebHook-${n}`,
              url: s[`webhook${n}`] || ''
            }))
            .filter(w => w.url.trim() || w.name !== `WebHook-${w.id}`);

          // Start with one empty slot; the admin adds more with "+ Add".
          setWebhooks(defaultWebhooks.length > 0 ? defaultWebhooks : [
            { id: Date.now().toString(), name: 'WebHook-1', url: '' }
          ]);
        }
        
        // Load per-user AI assignment
        setUserAI({
          provider: response.data.settings.aiProvider?.provider || '',
          model: response.data.settings.aiProvider?.model || '',
          displayName: response.data.settings.aiProvider?.displayName || '',
          systemPrompt: response.data.settings.aiProvider?.systemPrompt || '',
          mcpServers: response.data.settings.aiProvider?.mcpServers || [],
          mcpMode: response.data.settings.aiProvider?.mcpMode || 'native',
          confirmWrites: response.data.settings.aiProvider?.confirmWrites !== false
        });
        setUserFMS({
          fmsId: response.data.settings.fms?.fmsId || '',
          sources: response.data.settings.fms?.sources || []
        });
        setUserConnectors(response.data.settings.connectors || []);
        setUserOrgConnectors(response.data.settings.orgConnectors || []);
        setUserFeatures({
          mcp: response.data.settings.features?.mcp || 'inherit',
          connectors: response.data.settings.features?.connectors || 'inherit',
          images: response.data.settings.features?.images || 'inherit',
          search: response.data.settings.features?.search || 'inherit'
        });
        setUserOrgSources(response.data.settings.fms?.orgSources || []);
        setSheetSearch('');
        setConnSearch('');
        setMcpSearch('');
        fetchShared();
        setUserDeleteProtected(!!response.data.settings.chatDeleteProtected);
        setUserDeletePw('');
        setUserDeletePwClear(false);
        setUserVoiceEnabled(response.data.settings.voiceEnabled !== false);
        setUserMsgLimit(response.data.settings.monthlyMessageLimit || 0);
        setUserUsage(response.data.settings.usage || null);
        const b = response.data.settings.billing || { planId: null, status: 'none', currentPeriodEnd: null };
        setUserBilling(b);
        setSelPlanId(b.planId || '');
        setPayLink('');
        setResetPw('');
        setResetMsg('');
        fetchPlans();
        setUserVoiceId(response.data.settings.elevenLabsVoiceId || '');
        setUserSttLang(response.data.settings.sttLang || 'en-US');
        // Load knowledge base sources
        setKbText(''); setKbSourceName(''); setKbUrl(''); setKbMessage('');
        setKbQuery(''); setKbHits(null); // results belong to the user they were run for
        knowledgeAPI.list(user._id).then(setKbSources).catch(() => setKbSources([]));

        // Load the shared MCP server list for the assignment checkboxes
        fetchOrgMCPServers();

        setShowEditSettings(true);
      }
    } catch (error) {
      setError('Failed to load user settings');
    }
  };

  if (user?.role !== 'admin') {
    return (
      <div className="p-8 text-center">
        <h2 className="text-2xl font-bold text-red-400">Access Denied</h2>
        <p className="text-gray-400">You need admin privileges to access this page.</p>
      </div>
    );
  }

  // Does the plan the user is ON include connectors/sheets? Only meaningful
  // while their Feature access is "inherit" — an explicit on/off overrides it.
  // No plan at all is the legacy 'none' case, which is permissive.
  const selectedUserPlan = plansList.find(p => p._id === userBilling.planId);
  const planAllowsConnectors = selectedUserPlan
    ? selectedUserPlan.connectorsEnabled !== false
    : true;

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6 pl-10">
        <h1 className="text-3xl font-bold">User Management</h1>
        <div className="space-x-2">
          <button
            onClick={() => { setSharedMessage(''); fetchShared(); setShowShared(true); }}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg"
          >
            🔗 Shared Connectors
          </button>
          <button
            onClick={openBilling}
            className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-lg"
          >
            💳 Billing
          </button>
          <button
            onClick={openMCP}
            className="bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-lg"
          >
            🔌 MCP Servers
          </button>
          <button
            onClick={openAIKeys}
            className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg"
          >
            🔑 AI API Keys
          </button>
          <button
            onClick={openApiKeys}
            className="bg-rose-600 hover:bg-rose-700 text-white px-4 py-2 rounded-lg"
          >
            🔌 API Access
          </button>
          <button
            onClick={() => setShowAddUser(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
          >
            Add User
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-500 text-white p-3 rounded-lg mb-4">
          {error}
          <button onClick={() => setError('')} className="float-right">×</button>
        </div>
      )}

      {/* Public signup master switch. Sits in the open rather than inside a
          modal because it decides whether strangers can create accounts —
          an admin should never have to go looking for its current state. */}
      <div className={`flex items-center justify-between gap-4 p-4 rounded-lg mb-6 border ${
        signupEnabled
          ? 'bg-green-500/10 border-green-500/40'
          : 'bg-gray-700/30 border-gray-600/40'
      }`}>
        <div>
          <p className="font-semibold">
            Public sign-up {signupEnabled
              ? <span className="text-green-400">ON</span>
              : <span className="text-gray-400">OFF</span>}
          </p>
          <p className="text-xs text-gray-400 mt-0.5">
            {signupEnabled
              ? 'Anyone can create an account and start a free trial (7 days / 100 messages).'
              : 'Only you can create accounts. Everyone else can just sign in.'}
          </p>
          {/* Signup sends a confirmation email, so without mail it is on in
              name only. Say so here rather than let it fail silently. */}
          {signupEnabled && !mailConfigured && (
            <p className="text-xs text-amber-400 mt-1">
              ⚠️ Email is not set up, so sign-up will not actually work.{' '}
              <button onClick={openMail} className="underline hover:text-amber-300">Configure email</button>
            </p>
          )}
          <button onClick={openMail} className="text-xs text-gray-500 hover:text-gray-300 underline mt-1">
            ✉️ Email settings {mailConfigured ? '(configured)' : '(not set up)'}
          </button>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={signupEnabled}
          aria-label="Public sign-up"
          disabled={signupSaving}
          onClick={() => toggleSignup(!signupEnabled)}
          className={`relative inline-flex h-7 w-14 flex-shrink-0 rounded-full transition-colors disabled:opacity-50 ${
            signupEnabled ? 'bg-green-600' : 'bg-gray-600'
          }`}
        >
          <span className={`inline-block h-5 w-5 mt-1 rounded-full bg-white transition-transform ${
            signupEnabled ? 'translate-x-8' : 'translate-x-1'
          }`} />
        </button>
      </div>

      {/* Tabs: Users | Analytics */}
      <div className="flex gap-1 mb-4 border-b border-gray-700">
        {[['users', '👥 Users'], ['analytics', '📊 Analytics'], ['logs', '📜 User Logs']].map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`px-4 py-2 rounded-t-lg text-sm font-medium ${
              tab === key
                ? 'bg-gray-800 text-white border-b-2 border-blue-500'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'users' && (
      <>
      {/* Search */}
      <div className="mb-4">
        <input
          type="text"
          placeholder="Search users..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full p-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
        />
      </div>

      {/* Users Table */}
      {loading ? (
        <div className="text-center py-8">Loading users...</div>
      ) : (
        <>
          <div className="bg-gray-800 rounded-lg overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-700">
                  <th className="p-3 text-left">Name</th>
                  <th className="p-3 text-left">Email</th>
                  <th className="p-3 text-left">Role</th>
                  <th className="p-3 text-left">Status</th>
                  <th className="p-3 text-left">Last Updated</th>
                  <th className="p-3 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user._id} className="border-b border-gray-700 hover:bg-gray-750">
                    <td className="p-3">{user.name}</td>
                    <td className="p-3">{user.email}</td>
                    <td className="p-3">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        user.role === 'admin' 
                          ? 'bg-purple-600 text-white' 
                          : 'bg-gray-600 text-gray-300'
                      }`}>
                        {user.role}
                      </span>
                    </td>
                    <td className="p-3">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        user.isActive 
                          ? 'bg-green-600 text-white' 
                          : 'bg-red-600 text-white'
                      }`}>
                        {user.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="p-3">
                      {new Date(user.updatedAt).toLocaleDateString()}
                    </td>
                    <td className="p-3 space-x-2">
                      <button
                        onClick={() => openEditSettings(user)}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm"
                      >
                        Settings
                      </button>
                      {user.isActive ? (
                        <button
                          onClick={() => deactivateUser(user._id)}
                          className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm"
                        >
                          Deactivate
                        </button>
                      ) : (
                        /* Without Reactivate the account is stranded: it still
                           holds its email, so the person cannot sign up again,
                           and nothing else in the UI can switch it back on.
                           Permanent delete sits here too — only ever offered on
                           an already-deactivated row, so an active user can
                           never be erased by one misclick. */
                        <>
                          <button
                            onClick={() => reactivateUser(user._id)}
                            className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-sm"
                          >
                            Reactivate
                          </button>
                          <button
                            onClick={() => openPurge(user)}
                            title="Permanently delete this user and all their data"
                            className="text-red-400 hover:text-red-300 underline px-2 py-1 text-sm"
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center mt-4 space-x-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 bg-gray-700 rounded disabled:opacity-50"
              >
                Previous
              </button>
              <span className="px-3 py-1">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 bg-gray-700 rounded disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
      </>
      )}

      {/* Analytics dashboard */}
      {tab === 'analytics' && (
        <div className="space-y-5">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-400">AI usage per month — data comes from the message meter.</p>
            <input
              type="month"
              value={analyticsMonth}
              onChange={(e) => setAnalyticsMonth(e.target.value || new Date().toISOString().slice(0, 7))}
              className="px-3 py-1.5 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
            />
          </div>

          {!analytics ? (
            <div className="text-center py-8 text-gray-400">Loading analytics...</div>
          ) : (
            <>
              {/* Stat tiles */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  ['Total users', analytics.totals.users],
                  ['Active this month', analytics.totals.activeUsers],
                  ['Messages this month', analytics.totals.messages],
                ].map(([label, value]) => (
                  <div key={label} className="bg-gray-800 rounded-lg p-4">
                    <div className="text-2xl font-bold text-white">{Number(value).toLocaleString()}</div>
                    <div className="text-xs text-gray-400 mt-1">{label}</div>
                  </div>
                ))}
              </div>

              {/* 6-month trend */}
              <div className="bg-gray-800 rounded-lg p-4">
                <h3 className="font-semibold mb-3">Last 6 months — AI messages</h3>
                <div className="space-y-2">
                  {analytics.trend.map((tr) => {
                    const max = Math.max(...analytics.trend.map(x => x.messages), 1);
                    return (
                      <div key={tr.month} className="flex items-center gap-2">
                        <span className="w-16 text-xs text-gray-400">{tr.month}</span>
                        <div className="flex-1 bg-gray-700 rounded h-4 overflow-hidden">
                          <div
                            className="bg-blue-600 h-4 rounded"
                            style={{ width: `${(tr.messages / max) * 100}%` }}
                          />
                        </div>
                        <span className="w-14 text-xs text-right text-gray-300">{tr.messages.toLocaleString()}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Per-user usage table */}
              <div className="bg-gray-800 rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-700 text-left text-sm">
                      <th className="p-3">User</th>
                      <th className="p-3">Messages</th>
                      <th className="p-3">Providers</th>
                      <th className="p-3">Limit</th>
                      <th className="p-3">Used</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analytics.perUser.length === 0 && (
                      <tr>
                        <td colSpan={6} className="p-4 text-center text-gray-500 text-sm">
                          No usage recorded for this month.
                        </td>
                      </tr>
                    )}
                    {analytics.perUser.map((u) => {
                      // effectiveLimit = manual override > plan quota (falls back to
                      // the raw manual limit for older API responses)
                      const lim = u.effectiveLimit ?? u.limit;
                      const pct = lim > 0 ? Math.round((u.messages / lim) * 100) : null;
                      return (
                        <tr key={u._id} className="border-b border-gray-700 text-sm">
                          <td className="p-3">
                            <div className="text-white">{u.name}</div>
                            <div className="text-xs text-gray-500">{u.email}</div>
                          </td>
                          <td className="p-3">{u.messages.toLocaleString()}</td>
                          <td className="p-3">
                            {Object.entries(u.byProvider || {})
                              .filter(([, n]) => n > 0)
                              .map(([p, n]) => (
                                <span key={p} className="inline-block bg-gray-700 text-gray-300 rounded px-1.5 py-0.5 text-xs mr-1">
                                  {p}: {n}
                                </span>
                              ))}
                          </td>
                          <td className="p-3">
                            {lim > 0 ? lim.toLocaleString() : '∞'}
                            {u.planName && <div className="text-xs text-gray-500">{u.planName}</div>}
                          </td>
                          <td className={`p-3 font-medium ${pct !== null && pct >= 90 ? 'text-red-400' : 'text-gray-300'}`}>
                            {pct !== null ? `${pct}%` : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* User Logs: pick a user, read their full chat, download as CSV */}
      {tab === 'logs' && (
        <div className="bg-gray-800 rounded-lg p-4">
          <div className="flex flex-col sm:flex-row sm:items-end gap-3 mb-4">
            <div className="flex-1">
              <label className="block text-sm text-gray-300 mb-1">Select user</label>
              <select
                value={logUserId}
                onChange={(e) => loadUserLog(e.target.value)}
                className="w-full p-2 bg-gray-700 border border-gray-600 rounded text-white"
              >
                <option value="">— choose a user —</option>
                {logUsers.map((u) => (
                  <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                ))}
              </select>
            </div>
            <button
              onClick={downloadUserLog}
              disabled={!logUserId || logDownloading || !logData?.conversations?.length}
              className="px-4 py-2 bg-teal-600 hover:bg-teal-700 disabled:opacity-40 disabled:cursor-not-allowed rounded text-white text-sm font-medium whitespace-nowrap"
            >
              {logDownloading ? 'Preparing…' : '⬇ Download Excel/CSV'}
            </button>
          </div>

          {logLoading && <p className="text-gray-400 text-sm">Loading chat…</p>}

          {!logLoading && logData && logData.conversations.length === 0 && (
            <p className="text-gray-500 text-sm">This user has no chat history.</p>
          )}

          {!logLoading && logData && logData.conversations.length > 0 && (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
              {logData.conversations.map((c) => (
                <div key={c.id} className="border border-gray-700 rounded-lg">
                  <div className="px-3 py-2 bg-gray-750 border-b border-gray-700 flex justify-between items-center">
                    <span className="font-medium text-sm text-white truncate">{c.title}</span>
                    <span className="text-xs text-gray-500 whitespace-nowrap ml-2">
                      {c.messageCount} msg · {new Date(c.updatedAt).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="p-3 space-y-2">
                    {c.messages.map((m, i) => (
                      <div
                        key={i}
                        className={`flex ${m.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[80%] px-3 py-2 rounded-lg text-sm whitespace-pre-wrap break-words ${
                            m.sender === 'user' ? 'bg-teal-700 text-white' : 'bg-gray-700 text-gray-100'
                          }`}
                        >
                          {m.text}
                          <div className="text-[10px] opacity-60 mt-1">
                            {m.timestamp ? new Date(m.timestamp).toLocaleString() : ''}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {!logData && !logLoading && (
            <p className="text-gray-500 text-sm">Choose a user above to see their conversations.</p>
          )}
        </div>
      )}

      {/* Add User Modal */}
      {showAddUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 p-6 rounded-lg w-96">
            <h2 className="text-xl font-bold mb-4">Add New User</h2>
            <form onSubmit={createUser}>
              <div className="space-y-3">
                <input
                  type="text"
                  placeholder="Name"
                  value={newUser.name}
                  onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                  className="w-full p-2 bg-gray-700 border border-gray-600 rounded text-white"
                  required
                />
                <input
                  type="email"
                  placeholder="Email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                  className="w-full p-2 bg-gray-700 border border-gray-600 rounded text-white"
                  required
                />
                <input
                  type="password"
                  placeholder="Password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                  className="w-full p-2 bg-gray-700 border border-gray-600 rounded text-white"
                  required
                />
                <select
                  value={newUser.role}
                  onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                  className="w-full p-2 bg-gray-700 border border-gray-600 rounded text-white"
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="flex justify-end space-x-2 mt-4">
                <button
                  type="button"
                  onClick={() => setShowAddUser(false)}
                  className="px-4 py-2 bg-gray-600 rounded"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded"
                >
                  Create User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Settings Modal */}
      {showEditSettings && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 p-6 rounded-lg w-96 max-h-[600px] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">
              Settings for {selectedUser.name}
            </h2>
            <form onSubmit={updateUserSettings}>
              <div className="space-y-4">
                {/* Dynamic Webhooks Section */}
                <div className="bg-gray-750 p-4 rounded-lg">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="font-semibold">Webhooks</h3>
                    <button
                      type="button"
                      onClick={handleAddWebhook}
                      className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-sm flex items-center gap-1"
                    >
                      <span>+</span> Add
                    </button>
                  </div>
                  
                  <div className="space-y-3">
                    {webhooks.map((webhook) => (
                      <div key={webhook.id} className="relative">
                        <div className="flex gap-2">
                          <div className="flex-1">
                            <input
                              type="text"
                              value={webhook.name}
                              onChange={(e) => handleWebhookChange(webhook.id, 'name', e.target.value)}
                              placeholder="Webhook Name"
                              className="w-full p-2 bg-gray-700 border border-gray-600 rounded text-white font-medium mb-2"
                            />
                            <input
                              type="url"
                              value={webhook.url}
                              onChange={(e) => handleWebhookChange(webhook.id, 'url', e.target.value)}
                              placeholder="Webhook URL (HTTPS only)"
                              className="w-full p-2 bg-gray-700 border border-gray-600 rounded text-white"
                              pattern="https://.*"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => handleDeleteWebhook(webhook.id)}
                            className="self-start p-2 text-red-500 hover:text-red-400 transition-colors"
                            title="Delete webhook"
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2M10 11v6M14 11v6"/>
                            </svg>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Feature access — its own section, deliberately OUTSIDE the
                    AI Assistant block. Connectors answer from a sheet without
                    ever calling a model, so a user with no AI provider can
                    still be using them; hiding this control behind "has a
                    provider" left those users un-toggleable. */}
                <div className="bg-gray-750 p-4 rounded-lg">
                  <h3 className="font-semibold mb-1">Feature access</h3>
                  <p className="text-xs text-gray-400 mb-3">
                    Controls whether this user sees the <b>Integrations</b> page and can use
                    these features at all. "Follow plan" is the normal setting — use On/Off
                    only to make an exception for this one user, which wins over their plan.
                    Turning both off hides Integrations from their sidebar completely.
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { key: 'mcp', label: 'MCP Servers' },
                      { key: 'connectors', label: 'Connectors & Sheets' },
                      { key: 'images', label: 'Image generation' },
                      { key: 'search', label: 'Web search' }
                    ].map(f => (
                      <div key={f.key}>
                        <label className="block text-xs text-gray-400 mb-1">{f.label}</label>
                        <select
                          value={userFeatures[f.key]}
                          onChange={(e) => setUserFeatures({ ...userFeatures, [f.key]: e.target.value })}
                          className="w-full p-2 bg-gray-700 border border-gray-600 rounded text-white text-sm"
                        >
                          <option value="inherit">Follow plan</option>
                          <option value="on">Always on</option>
                          <option value="off">Always off</option>
                        </select>
                      </div>
                    ))}
                  </div>
                </div>

                {/* AI Assistant Assignment (per-user) */}
                <div className="bg-gray-750 p-4 rounded-lg">
                  <h3 className="font-semibold mb-1">AI Assistant</h3>
                  <p className="text-xs text-gray-400 mb-3">
                    Assign which shared AI key this user chats with, and their individual system prompt.
                  </p>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm text-gray-300 mb-1">Provider</label>
                      <select
                        value={userAI.provider}
                        onChange={(e) => setUserAI(prev => ({ ...prev, provider: e.target.value }))}
                        className="w-full p-2 bg-gray-700 border border-gray-600 rounded text-white"
                      >
                        {AI_PROVIDER_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>

                    {userAI.provider && (
                      <>
                        <div>
                          <label className="block text-sm text-gray-300 mb-1">
                            Display Name <span className="text-xs text-gray-500">(shown in the user's chat dropdown)</span>
                          </label>
                          <input
                            type="text"
                            value={userAI.displayName}
                            onChange={(e) => setUserAI(prev => ({ ...prev, displayName: e.target.value }))}
                            placeholder={`AI Assistant (${AI_PROVIDER_OPTIONS.find(o => o.value === userAI.provider)?.label.split(' ')[0] || 'AI'})`}
                            maxLength={60}
                            className="w-full p-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-500"
                          />
                          <p className="text-xs text-gray-500 mt-1">Blank = default name. e.g. "ROUT Support Bot"</p>
                        </div>

                        <div>
                          <label className="block text-sm text-gray-300 mb-1">
                            Model <span className="text-xs text-gray-500">(optional)</span>
                          </label>
                          <input
                            type="text"
                            value={userAI.model}
                            onChange={(e) => setUserAI(prev => ({ ...prev, model: e.target.value }))}
                            placeholder={AI_MODEL_PLACEHOLDERS[userAI.provider] || 'Model name'}
                            className="w-full p-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-500"
                          />
                        </div>

                        <div>
                          <label className="block text-sm text-gray-300 mb-1">
                            System Prompt <span className="text-xs text-gray-500">(for this user only)</span>
                          </label>
                          <textarea
                            value={userAI.systemPrompt}
                            onChange={(e) => setUserAI(prev => ({ ...prev, systemPrompt: e.target.value }))}
                            placeholder="e.g. You are a support assistant for this customer. Answer in Hindi and keep replies short."
                            rows={4}
                            maxLength={8000}
                            className="w-full p-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-500 resize-y"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            {userAI.systemPrompt.length}/8000 characters
                          </p>
                        </div>

                        {/* MCP server assignment — works on every provider:
                            Claude via Anthropic's connector, the rest via the
                            backend tool bridge. */}
                        {!!userAI.provider && (
                          <div>
                            <label className="block text-sm text-gray-300 mb-1">
                              MCP Servers <span className="text-xs text-gray-500">(tools this user's AI can use)</span>
                            </label>
                            {orgMCPServers.length === 0 ? (
                              <p className="text-xs text-gray-500">
                                No shared MCP servers yet — add them via the "MCP Servers" button on the Users page.
                              </p>
                            ) : (() => {
                              // Same search-to-assign pattern as connectors: only the
                              // assigned servers are listed; the rest are searched for,
                              // so 200 shared servers don't all render at once.
                              const isOn = id => userAI.mcpServers.includes(id);
                              const q = mcpSearch.trim().toLowerCase();
                              const mine = orgMCPServers.filter(s => isOn(s.id));
                              const rest = q
                                ? orgMCPServers.filter(s => !isOn(s.id) &&
                                    (`${s.name} ${s.url || ''}`).toLowerCase().includes(q)).slice(0, 8)
                                : [];
                              const restTotal = orgMCPServers.length - mine.length;
                              return (
                                <div className="space-y-2">
                                  {mine.length === 0 && (
                                    <p className="text-xs text-gray-500">No MCP servers assigned — search below to assign.</p>
                                  )}
                                  {mine.map(s => (
                                    <div key={s.id} className="flex items-center justify-between border border-teal-700/60 bg-teal-900/10 rounded-lg p-2">
                                      <span className="text-sm text-gray-200 truncate">
                                        <span className="font-medium">{s.name}</span>
                                        <span className="text-xs text-gray-500 ml-2 truncate">{s.url}</span>
                                      </span>
                                      <button type="button"
                                        onClick={() => toggleUserMCP(s.id)}
                                        className="text-xs text-red-400 hover:text-red-300 ml-2 whitespace-nowrap">Remove</button>
                                    </div>
                                  ))}

                                  {restTotal > 0 && (
                                    <div className="pt-1">
                                      <input type="text" value={mcpSearch}
                                        onChange={(e) => setMcpSearch(e.target.value)}
                                        placeholder={`+ Assign a MCP — search ${restTotal} available`}
                                        className="w-full p-2 bg-gray-700 border border-dashed border-gray-600 rounded text-white placeholder-gray-500 text-sm" />
                                      {mcpSearch.trim() && (
                                        <div className="mt-1 space-y-1">
                                          {rest.length === 0 && <p className="text-xs text-gray-500 px-1">No matching MCP servers found.</p>}
                                          {rest.map(s => (
                                            <button key={s.id} type="button"
                                              onClick={() => { toggleUserMCP(s.id); setMcpSearch(''); }}
                                              className="w-full text-left px-2 py-1.5 rounded bg-gray-700/60 hover:bg-gray-700 text-sm text-gray-200 truncate">
                                              {s.name}
                                              <span className="text-xs text-gray-500 ml-2">{s.url}</span>
                                            </button>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            })()}

                            {/* Irreversible tool calls (send mail, create event,
                                upload) ask before running. Only meaningful when
                                the user actually has tools. */}
                            {userAI.mcpServers.length > 0 && (
                              <label className="flex items-start gap-2 mt-3 text-sm text-gray-300 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={userAI.confirmWrites}
                                  onChange={(e) => setUserAI({ ...userAI, confirmWrites: e.target.checked })}
                                  className="accent-teal-500 mt-0.5"
                                />
                                <span>
                                  Ask before sending / creating
                                  <span className="block text-xs text-gray-500">
                                    Mail, calendar events and uploads wait for a "yes" first. An email
                                    cannot be unsent — leave this on unless speed matters more.
                                  </span>
                                </span>
                              </label>
                            )}

                            {/* Claude can reach MCP two ways; every other
                                provider only has the bridge, so this choice
                                is meaningless for them. */}
                            {userAI.provider === 'claude' && userAI.mcpServers.length > 0 && (
                              <div className="mt-3">
                                <label className="block text-sm text-gray-300 mb-1">
                                  How Claude connects
                                </label>
                                <select
                                  value={userAI.mcpMode}
                                  onChange={(e) => setUserAI({ ...userAI, mcpMode: e.target.value })}
                                  className="w-full p-2 bg-gray-700 border border-gray-600 rounded text-white"
                                >
                                  <option value="native">Anthropic connector — faster, streams</option>
                                  <option value="bridge">Via this server — tool calls logged</option>
                                </select>
                                <p className="text-xs text-gray-500 mt-1">
                                  {userAI.mcpMode === 'bridge'
                                    ? 'Tool calls run through this server, so they appear in the logs and can be checked when something breaks. The reply arrives in one piece instead of streaming.'
                                    : "Anthropic's servers call the MCP endpoint directly. Fastest option, but tool calls are invisible to this app — nothing to inspect when a client reports a problem."}
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* FMS / Google Sheet task sources (per-user) */}
                <div className="bg-gray-750 p-4 rounded-lg">
                  <h3 className="font-semibold mb-1">Task Sheets (FMS)</h3>
                  <p className="text-xs text-gray-400 mb-3">
                    Connect Google Sheets so this user can ask the chatbot for their tasks
                    (e.g. "my pending task"). Each source needs the sheet's deployed
                    Apps Script URL (see google-apps-script/fms-connector.gs).
                  </p>

                  {/* Task sheets run through the same gate as connectors. Without
                      this warning the whole section can be filled in correctly —
                      URL, token, columns, FMS ID, sheets ticked — and still never
                      fire in chat, with nothing on screen saying why. */}
                  {userFeatures.connectors === 'off' && (
                    <div className="mb-3 p-2 bg-amber-500/10 border border-amber-500/40 rounded">
                      <p className="text-xs text-amber-300">
                        ⚠️ <b>Connectors &amp; Sheets is set to "Always off"</b> for this user, so
                        nothing configured here will work in chat. Change it under
                        <b> Feature access</b> above.
                      </p>
                    </div>
                  )}
                  {userFeatures.connectors === 'inherit' && !planAllowsConnectors && (
                    <div className="mb-3 p-2 bg-amber-500/10 border border-amber-500/40 rounded">
                      <p className="text-xs text-amber-300">
                        ⚠️ This user's plan does not include Connectors &amp; Sheets, so task
                        sheets will not answer in chat. Upgrade their plan, or set
                        <b> Feature access → Connectors &amp; Sheets</b> to "Always on".
                      </p>
                    </div>
                  )}

                  <div className="mb-3">
                    <label className="block text-sm text-gray-300 mb-1">
                      FMS ID <span className="text-xs text-gray-500">(this user's ID in the sheets, e.g. EMP101)</span>
                    </label>
                    <input
                      type="text"
                      value={userFMS.fmsId}
                      onChange={(e) => setUserFMS(prev => ({ ...prev, fmsId: e.target.value }))}
                      placeholder="EMP101"
                      maxLength={100}
                      className="w-full p-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-500"
                    />
                  </div>

                  {/* Shared sheets: tick one and set only THIS user's permissions.
                      The URL/token/columns live once in Shared Connectors. */}
                  <div className="mb-4">
                    <label className="block text-sm text-gray-300 mb-1">
                      Shared sheets <span className="text-xs text-gray-500">(company sheets — set up once)</span>
                    </label>
                    {sharedSheets.length === 0 ? (
                      <p className="text-xs text-gray-500">
                        None yet — add them via "🔗 Shared Connectors" on the Users page.
                      </p>
                    ) : (() => {
                      const { mine, rest, restTotal } = splitAssigned(sharedSheets, userOrgSources, sheetSearch);
                      return (
                        <div className="space-y-2">
                          {mine.length === 0 && (
                            <p className="text-xs text-gray-500">Koi sheet assign nahi — neeche search karke jodo.</p>
                          )}
                          {mine.map(sh => {
                            const a = userOrgSources.find(x => x.id === sh.id);
                            return (
                              <div key={sh.id} className="border border-teal-700/60 bg-teal-900/10 rounded-lg p-2">
                                <div className="flex items-center justify-between">
                                  <span className="text-sm text-gray-200">
                                    <span className="font-medium">{sh.name}</span>
                                    <span className="text-xs text-gray-500 ml-2">{sh.keywords}</span>
                                  </span>
                                  <button type="button"
                                    onClick={() => toggleAssign(userOrgSources, setUserOrgSources, sh.id)}
                                    className="text-xs text-red-400 hover:text-red-300">Remove</button>
                                </div>
                                <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-gray-300">
                                  <select value={a.viewMode}
                                    onChange={(e) => setAssignField(userOrgSources, setUserOrgSources, sh.id, 'viewMode', e.target.value)}
                                    className="p-1 bg-gray-700 border border-gray-600 rounded text-white text-xs">
                                    <option value="own">Own rows only</option>
                                    <option value="all">Own + all rows</option>
                                  </select>
                                  {['canAdd', 'canEdit', 'canDelete'].map(f => (
                                    <label key={f} className="flex items-center gap-1 text-xs cursor-pointer">
                                      <input type="checkbox" checked={!!a[f]}
                                        onChange={(e) => setAssignField(userOrgSources, setUserOrgSources, sh.id, f, e.target.checked)}
                                        className="accent-teal-500" />
                                      {f.replace('can', '')}
                                    </label>
                                  ))}
                                </div>
                              </div>
                            );
                          })}

                          {restTotal > 0 && (
                            <div className="pt-1">
                              <input type="text" value={sheetSearch}
                                onChange={(e) => setSheetSearch(e.target.value)}
                                placeholder={`+ Assign a sheet — search ${restTotal} available`}
                                className="w-full p-2 bg-gray-700 border border-dashed border-gray-600 rounded text-white placeholder-gray-500 text-sm" />
                              {sheetSearch.trim() && (
                                <div className="mt-1 space-y-1">
                                  {rest.length === 0 && <p className="text-xs text-gray-500 px-1">Kuch nahi mila.</p>}
                                  {rest.map(sh => (
                                    <button key={sh.id} type="button"
                                      onClick={() => { toggleAssign(userOrgSources, setUserOrgSources, sh.id); setSheetSearch(''); }}
                                      className="w-full text-left px-2 py-1.5 rounded bg-gray-700/60 hover:bg-gray-700 text-sm text-gray-200">
                                      {sh.name}
                                      <span className="text-xs text-gray-500 ml-2">{sh.keywords}</span>
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>

                  {/* Per-user sheets removed — see Shared Connectors */}
                </div>

                {/* Connectors — Google Docs (gmail/calendar will reuse this section) */}
                <div className="bg-gray-750 p-4 rounded-lg">
                  <h3 className="font-semibold mb-1">Connectors</h3>
                  <p className="text-xs text-gray-400 mb-3">
                    Connect Google Docs so this user can ask the chatbot to read or write
                    documents (e.g. "price list mein kya likha hai", "policy doc mein likho: ...").
                    Deploy google-apps-script/docs-connector.gs in the client's own Google
                    account — no OAuth setup needed.
                  </p>

                  {/* Shared connectors — tick to assign, permissions per user */}
                  <div className="mb-4">
                    <label className="block text-sm text-gray-300 mb-1">
                      Shared connectors <span className="text-xs text-gray-500">(company-wide — set up once)</span>
                    </label>
                    {sharedConns.length === 0 ? (
                      <p className="text-xs text-gray-500">
                        None yet — add them via "🔗 Shared Connectors" on the Users page.
                      </p>
                    ) : (() => {
                      const { mine, rest, restTotal } = splitAssigned(sharedConns, userOrgConnectors, connSearch);
                      return (
                        <div className="space-y-2">
                          {mine.length === 0 && (
                            <p className="text-xs text-gray-500">Koi connector assign nahi — neeche search karke jodo.</p>
                          )}
                          {mine.map(sc => {
                            const a = userOrgConnectors.find(x => x.id === sc.id);
                            return (
                              <div key={sc.id} className="border border-teal-700/60 bg-teal-900/10 rounded-lg p-2">
                                <div className="flex items-center justify-between">
                                  <span className="text-sm text-gray-200">
                                    <span className="font-medium">{sc.name}</span>
                                    <span className="text-xs text-gray-500 ml-2">{sc.type}</span>
                                  </span>
                                  <button type="button"
                                    onClick={() => toggleAssign(userOrgConnectors, setUserOrgConnectors, sc.id)}
                                    className="text-xs text-red-400 hover:text-red-300">Remove</button>
                                </div>
                                <div className="flex items-center gap-4 mt-2">
                                  {['canRead', 'canWrite'].map(f => (
                                    <label key={f} className="flex items-center gap-1 text-xs text-gray-300 cursor-pointer">
                                      <input type="checkbox" checked={f === 'canRead' ? a.canRead !== false : !!a.canWrite}
                                        onChange={(e) => setAssignField(userOrgConnectors, setUserOrgConnectors, sc.id, f, e.target.checked)}
                                        className="accent-teal-500" />
                                      {f === 'canRead' ? 'Read' : 'Write'}
                                    </label>
                                  ))}
                                </div>
                              </div>
                            );
                          })}

                          {restTotal > 0 && (
                            <div className="pt-1">
                              <input type="text" value={connSearch}
                                onChange={(e) => setConnSearch(e.target.value)}
                                placeholder={`+ Assign a connector — search ${restTotal} available`}
                                className="w-full p-2 bg-gray-700 border border-dashed border-gray-600 rounded text-white placeholder-gray-500 text-sm" />
                              {connSearch.trim() && (
                                <div className="mt-1 space-y-1">
                                  {rest.length === 0 && <p className="text-xs text-gray-500 px-1">Kuch nahi mila.</p>}
                                  {rest.map(sc => (
                                    <button key={sc.id} type="button"
                                      onClick={() => { toggleAssign(userOrgConnectors, setUserOrgConnectors, sc.id); setConnSearch(''); }}
                                      className="w-full text-left px-2 py-1.5 rounded bg-gray-700/60 hover:bg-gray-700 text-sm text-gray-200">
                                      {sc.name}
                                      <span className="text-xs text-gray-500 ml-2">{sc.type}</span>
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>

                  {/* Per-user connectors removed: everything is defined once in
                      "Shared Connectors" on the Users page and assigned above. */}
                </div>

                {/* Usage & monthly limit (cost control) */}
                <div className="bg-gray-750 p-4 rounded-lg">
                  <h3 className="font-semibold">Usage & Limits</h3>
                  <p className="text-xs text-gray-400 mt-0.5">
                    This month ({userUsage?.month || '—'}): <span className="text-teal-400 font-medium">{userUsage?.messages ?? 0}</span> AI messages
                  </p>
                  <div className="mt-3">
                    <label className="block text-sm text-gray-300 mb-1">Monthly message limit (0 = unlimited)</label>
                    <input
                      type="number"
                      min="0"
                      max="1000000"
                      step="1"
                      value={userMsgLimit}
                      onChange={(e) => setUserMsgLimit(e.target.value)}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-teal-500"
                    />
                    {userUsage && (() => {
                      // Effective quota: manual override wins, else the plan's quota
                      const plan = plansList.find(p => p._id === userBilling.planId);
                      const manual = Number(userMsgLimit) > 0 ? Number(userMsgLimit) : 0;
                      const effective = manual || plan?.monthlyMessages || 0;
                      if (!effective) {
                        return <p className="text-xs mt-1 text-gray-400">Effective limit: ∞ unlimited (no plan quota, no manual limit)</p>;
                      }
                      const source = manual ? 'manual limit' : `${plan.name} plan`;
                      const reached = userUsage.messages >= effective;
                      return (
                        <p className={`text-xs mt-1 ${reached ? 'text-red-400' : 'text-teal-400'}`}>
                          Effective limit: {effective.toLocaleString()}/month (from {source}) — {reached
                            ? 'limit reached; user is blocked until it is raised or next month starts.'
                            : `${Math.max(0, effective - userUsage.messages).toLocaleString()} messages remaining.`}
                        </p>
                      );
                    })()}
                  </div>
                </div>

                {/* Subscription plan (billing) */}
                <div className="bg-gray-750 p-4 rounded-lg">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold">Subscription Plan</h3>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      userBilling.status === 'active' ? 'bg-green-600 text-white'
                      : userBilling.status === 'expired' ? 'bg-red-600 text-white'
                      : 'bg-gray-600 text-gray-300'
                    }`}>
                      {userBilling.status === 'active' ? 'ACTIVE' : userBilling.status === 'expired' ? 'EXPIRED' : 'NO PLAN'}
                    </span>
                  </div>
                  {userBilling.planId && (
                    <p className="text-xs text-gray-400 mt-1">
                      Current: <span className="text-white">{plansList.find(p => p._id === userBilling.planId)?.name || 'Plan'}</span>
                      {userBilling.currentPeriodEnd && <> — valid till {new Date(userBilling.currentPeriodEnd).toLocaleDateString()}</>}
                    </p>
                  )}
                  <div className="mt-3 flex gap-2">
                    <select
                      value={selPlanId}
                      onChange={(e) => setSelPlanId(e.target.value)}
                      className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-teal-500"
                    >
                      <option value="">— Choose a plan —</option>
                      {plansList.filter(p => p.isActive).map(p => (
                        <option key={p._id} value={p._id}>
                          {p.name} — ₹{(p.pricePaise / 100).toLocaleString('en-IN')}/mo, {p.monthlyMessages > 0 ? `${p.monthlyMessages} msgs` : 'unlimited'}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <button
                      type="button"
                      disabled={!selPlanId}
                      onClick={() => assignPlan(selPlanId)}
                      className="bg-green-600 hover:bg-green-700 disabled:opacity-40 text-white px-3 py-1.5 rounded text-sm"
                    >
                      ✅ Activate 30 days (paid manually)
                    </button>
                    <button
                      type="button"
                      disabled={!selPlanId}
                      onClick={createPayLink}
                      className="bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white px-3 py-1.5 rounded text-sm"
                    >
                      🔗 Create payment link
                    </button>
                    {userBilling.planId && (
                      <button
                        type="button"
                        onClick={() => { assignPlan(null); setSelPlanId(''); }}
                        className="bg-gray-600 hover:bg-gray-500 text-white px-3 py-1.5 rounded text-sm"
                      >
                        Remove plan
                      </button>
                    )}
                  </div>
                  {payLink && payLink !== '...' && (
                    <div className="mt-2 flex gap-2">
                      <input
                        readOnly
                        value={payLink}
                        onFocus={(e) => e.target.select()}
                        className="flex-1 px-3 py-1.5 bg-gray-900 border border-gray-600 rounded text-teal-300 text-xs"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          try { navigator.clipboard.writeText(payLink); } catch (e) {
                            const i = document.createElement('input'); i.value = payLink;
                            document.body.appendChild(i); i.select(); document.execCommand('copy'); i.remove();
                          }
                        }}
                        className="bg-teal-600 hover:bg-teal-700 text-white px-3 py-1.5 rounded text-xs"
                      >
                        Copy
                      </button>
                    </div>
                  )}
                  {payLink === '...' && <p className="text-xs text-gray-400 mt-2">Creating payment link…</p>}
                  <p className="text-[11px] text-gray-500 mt-2">
                    Plan quota applies automatically. The "Monthly message limit" above (if &gt; 0) overrides the plan.
                  </p>
                </div>

                {/* Voice mode toggle + voice selection */}
                <div className="bg-gray-750 p-4 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold">Voice Mode</h3>
                      <p className="text-xs text-gray-400 mt-0.5">Mic dictation + spoken AI replies for this user</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setUserVoiceEnabled(v => !v)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${userVoiceEnabled ? 'bg-teal-500' : 'bg-gray-600'}`}
                      title={userVoiceEnabled ? 'Voice mode ON' : 'Voice mode OFF'}
                    >
                      <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${userVoiceEnabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
                    </button>
                  </div>

                  {userVoiceEnabled && (
                    <div className="mt-3 space-y-3">
                      <div>
                        <label className="block text-sm text-gray-300 mb-1">Dictation language (mic input)</label>
                        <select
                          value={userSttLang}
                          onChange={(e) => setUserSttLang(e.target.value)}
                          className="w-full p-2 bg-gray-700 border border-gray-600 rounded text-white"
                        >
                          {STT_LANG_OPTIONS.map(l => (
                            <option key={l.value} value={l.value}>{l.label}</option>
                          ))}
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                          What the mic expects the user to SPEAK in. Wrong language here is the #1 cause of garbled dictation.
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm text-gray-300 mb-1">Reply voice (spoken AI replies)</label>
                        <select
                          value={userVoiceId}
                          onChange={(e) => setUserVoiceId(e.target.value)}
                          className="w-full p-2 bg-gray-700 border border-gray-600 rounded text-white"
                        >
                          {VOICE_OPTIONS.map(v => (
                            <option key={v.id || 'default'} value={v.id}>{v.label}</option>
                          ))}
                        </select>
                        <p className="text-xs text-gray-500 mt-1">Free plan voices. Cloned/premium voices need a paid ElevenLabs plan.</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Password reset (user forgot their password) */}
                <div className="bg-gray-750 p-4 rounded-lg">
                  <h3 className="font-semibold">Reset Password</h3>
                  <p className="text-xs text-gray-400 mt-0.5 mb-2">
                    Set a new password for this user (they'll sign in with it right away).
                  </p>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="New password (min 6 chars)"
                      value={resetPw}
                      onChange={(e) => { setResetPw(e.target.value); setResetMsg(''); }}
                      className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-teal-500"
                    />
                    <button
                      type="button"
                      onClick={resetUserPassword}
                      disabled={!resetPw}
                      className="bg-orange-600 hover:bg-orange-700 disabled:opacity-40 text-white px-3 py-2 rounded-lg text-sm whitespace-nowrap"
                    >
                      🔑 Reset
                    </button>
                  </div>
                  {resetMsg && (
                    <p className={`text-xs mt-2 ${resetMsg.startsWith('✅') ? 'text-teal-400' : 'text-red-400'}`}>{resetMsg}</p>
                  )}
                </div>

                {/* Chat delete protection (admin-set password) */}
                <div className="bg-gray-750 p-4 rounded-lg">
                  <h3 className="font-semibold">
                    Chat Delete Protection{' '}
                    {userDeleteProtected
                      ? <span className="text-xs text-teal-400 font-normal">(ON 🔒)</span>
                      : <span className="text-xs text-gray-500 font-normal">(off)</span>}
                  </h3>
                  <p className="text-xs text-gray-400 mt-0.5 mb-2">
                    Set a password this user must type before deleting any chat history.
                    Keep it with the admin — checked on the server, so it can't be bypassed.
                  </p>
                  <input
                    type="text"
                    placeholder={userDeleteProtected ? 'New password (blank = keep current)' : 'Set delete password (min 4 chars)'}
                    value={userDeletePw}
                    onChange={(e) => setUserDeletePw(e.target.value)}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-teal-500"
                  />
                  {userDeleteProtected && (
                    <label className="flex items-center gap-2 text-sm text-gray-300 mt-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={userDeletePwClear}
                        onChange={(e) => { setUserDeletePwClear(e.target.checked); if (e.target.checked) setUserDeletePw(''); }}
                        className="accent-red-500"
                      />
                      Protection hatao (user bina password ke delete kar payega)
                    </label>
                  )}
                  <p className="text-xs text-gray-500 mt-2">Save Settings dabane par apply hoga.</p>
                </div>

                {/* Knowledge Base (RAG) */}
                <div className="bg-gray-750 p-4 rounded-lg">
                  <h3 className="font-semibold">Knowledge Base</h3>
                  <p className="text-xs text-gray-400 mt-0.5 mb-3">
                    Upload docs so this user's AI answers from them (PDF, text, website, FAQ).
                  </p>

                  {/* Existing sources */}
                  {kbSources.length > 0 ? (
                    <div className="space-y-1 mb-3">
                      {kbSources.map((s) => (
                        <div key={s.source} className="flex items-center gap-2 text-sm bg-gray-700 rounded px-2 py-1.5">
                          <span className="flex-1 truncate">{s.source}</span>
                          {s.legacyChunks > 0 && (
                            <span
                              className="text-[10px] text-amber-300 bg-amber-500/15 border border-amber-500/30 rounded px-1"
                              title={`${s.legacyChunks} chunks use the old embedding scheme and retrieve worse. Run: node scripts/reindex-kb.js`}
                            >
                              old index
                            </span>
                          )}
                          <span className="text-xs text-gray-400">{s.chunks} chunks</span>
                          <button
                            type="button"
                            onClick={() => deleteKbSource(s.source)}
                            className="text-red-400 hover:text-red-300"
                            title="Remove"
                          >
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500 mb-3">No documents yet.</p>
                  )}

                  {kbMessage && (
                    <div className="text-xs text-blue-300 bg-blue-500/15 border border-blue-500/30 rounded px-2 py-1 mb-3">{kbMessage}</div>
                  )}

                  {/* Upload file */}
                  <div className="flex gap-2 mb-2">
                    <input ref={kbFileRef} type="file" accept=".pdf,.txt,.md,.csv" onChange={uploadKbFile} className="hidden" />
                    <button
                      type="button"
                      disabled={kbBusy}
                      onClick={() => kbFileRef.current?.click()}
                      className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded text-sm"
                    >
                      📄 Upload PDF / Text
                    </button>
                  </div>

                  {/* Add URL */}
                  <div className="flex gap-2 mb-2">
                    <input
                      type="url"
                      value={kbUrl}
                      onChange={(e) => setKbUrl(e.target.value)}
                      placeholder="https://example.com/faq"
                      className="flex-1 p-2 bg-gray-700 border border-gray-600 rounded text-white text-sm"
                    />
                    <button type="button" disabled={kbBusy || !kbUrl.trim()} onClick={addKbUrl} className="px-3 py-1.5 bg-gray-600 hover:bg-gray-500 disabled:opacity-50 text-white rounded text-sm">Add URL</button>
                  </div>

                  {/* Paste text / FAQ */}
                  <input
                    type="text"
                    value={kbSourceName}
                    onChange={(e) => setKbSourceName(e.target.value)}
                    placeholder="Name (e.g. Pricing FAQ)"
                    className="w-full p-2 bg-gray-700 border border-gray-600 rounded text-white text-sm mb-2"
                  />
                  <textarea
                    value={kbText}
                    onChange={(e) => setKbText(e.target.value)}
                    placeholder="Paste FAQ or any text the AI should know…"
                    rows={3}
                    className="w-full p-2 bg-gray-700 border border-gray-600 rounded text-white text-sm resize-y mb-2"
                  />
                  <button type="button" disabled={kbBusy || !kbText.trim()} onClick={addKbText} className="px-3 py-1.5 bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white rounded text-sm">
                    {kbBusy ? 'Processing…' : 'Add Text'}
                  </button>

                  {/* Retrieval tester */}
                  {kbSources.length > 0 && (
                    <div className="mt-4 pt-3 border-t border-gray-600">
                      <p className="text-xs text-gray-400 mb-2">
                        Test what a question finds. Nothing found means the answer came from the model alone.
                      </p>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={kbQuery}
                          onChange={(e) => setKbQuery(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') testKbQuery(); }}
                          placeholder="e.g. refund kitne din me milta hai"
                          className="flex-1 p-2 bg-gray-700 border border-gray-600 rounded text-white text-sm"
                        />
                        <button
                          type="button"
                          disabled={kbBusy || !kbQuery.trim()}
                          onClick={testKbQuery}
                          className="px-3 py-1.5 bg-gray-600 hover:bg-gray-500 disabled:opacity-50 text-white rounded text-sm"
                        >
                          Test
                        </button>
                      </div>

                      {kbHits && kbHits.length === 0 && (
                        <p className="text-xs text-amber-300 mt-2">
                          Nothing retrieved — the AI would answer this from general knowledge.
                        </p>
                      )}
                      {kbHits && kbHits.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {kbHits.map((h, i) => (
                            <div key={i} className="text-xs bg-gray-700 rounded px-2 py-1.5">
                              <div className="flex gap-2 text-gray-400">
                                <span className="flex-1 truncate">
                                  {h.source}{h.heading ? ` › ${h.heading}` : ''}
                                </span>
                                <span title="cosine similarity / standard deviations above this KB's average / keyword score">
                                  {h.cosine === null ? 'keyword only' : `${h.cosine} · z${h.z} · kw ${h.lexical}`}
                                </span>
                              </div>
                              <div className="text-gray-300 mt-0.5 line-clamp-2">{h.preview}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>

              </div>
              <div className="flex justify-end space-x-2 mt-4">
                <button
                  type="button"
                  onClick={() => setShowEditSettings(false)}
                  className="px-4 py-2 bg-gray-600 rounded"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded"
                >
                  Save Settings
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Permanent delete — the only action here with no undo, so it asks the
          admin to retype the email rather than offering a one-click Yes. */}
      {purgeTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60]">
          <div className="bg-gray-800 p-6 rounded-lg w-[480px] border border-red-500/40">
            <h2 className="text-xl font-bold mb-1 text-red-400">Delete permanently?</h2>
            <p className="text-sm text-gray-300 mb-3">
              This erases <span className="font-medium text-white">{purgeTarget.name}</span> and
              everything they own — chat history, knowledge base, usage records, settings and
              API keys. <span className="text-red-300">It cannot be undone.</span>
            </p>
            <p className="text-xs text-gray-400 mb-4">
              Their email will become free, so they can sign up again as a new account.
              If you only want to block access, use <span className="text-gray-200">Reactivate/Deactivate</span> instead.
            </p>

            <label className="block text-sm text-gray-300 mb-1">
              Type <span className="font-mono text-white">{purgeTarget.email}</span> to confirm
            </label>
            <input
              type="text"
              value={purgeTyped}
              onChange={(e) => setPurgeTyped(e.target.value)}
              autoComplete="off"
              placeholder={purgeTarget.email}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm mb-3"
            />

            {purgeError && (
              <div className="mb-3 p-2 bg-red-500/10 border border-red-500/30 rounded">
                <p className="text-red-400 text-sm">{purgeError}</p>
              </div>
            )}

            <div className="flex justify-end gap-2">
              <button
                onClick={() => setPurgeTarget(null)}
                className="bg-gray-600 hover:bg-gray-500 text-white px-4 py-2 rounded text-sm"
              >
                Cancel
              </button>
              <button
                onClick={confirmPurge}
                // Enabled only on an exact match, so the button itself is the
                // last guard rather than relying on the server to say no.
                disabled={purgeBusy || purgeTyped.trim().toLowerCase() !== purgeTarget.email.toLowerCase()}
                className="bg-red-600 hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed text-white px-4 py-2 rounded text-sm"
              >
                {purgeBusy ? 'Deleting…' : 'Delete permanently'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email (SMTP) Modal */}
      {showMail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 p-6 rounded-lg w-[560px] max-h-[85vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-1">✉️ Email Settings</h2>
            <p className="text-xs text-gray-400 mb-4">
              Used to send sign-up confirmation links. Without this, public sign-up cannot work.
              The password is stored encrypted and never shown again.
            </p>

            {mailMsg && (
              <div className="bg-blue-500/20 border border-blue-500/40 text-blue-300 text-sm p-2 rounded mb-3">
                {mailMsg}
              </div>
            )}

            <div className="space-y-3 mb-4">
              <div className="flex gap-2">
                <input type="text" placeholder="SMTP host (e.g. smtp.gmail.com)" value={mailForm.host}
                  onChange={(e) => setMailForm({ ...mailForm, host: e.target.value })}
                  className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm" />
                <input type="number" placeholder="Port" value={mailForm.port}
                  onChange={(e) => setMailForm({ ...mailForm, port: e.target.value })}
                  className="w-24 px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm" />
              </div>

              <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                <input type="checkbox" checked={!!mailForm.secure}
                  onChange={(e) => setMailForm({ ...mailForm, secure: e.target.checked })}
                  className="accent-teal-500" />
                Use SSL/TLS directly <span className="text-xs text-gray-500">(tick for port 465, leave off for 587)</span>
              </label>

              <input type="text" placeholder="Username (usually the full email address)" value={mailForm.user}
                onChange={(e) => setMailForm({ ...mailForm, user: e.target.value })}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm" />

              <input type="password" autoComplete="new-password"
                placeholder={mailHasPass ? 'Password — saved ✅ (type to replace)' : 'Password / app password'}
                value={mailForm.pass}
                onChange={(e) => setMailForm({ ...mailForm, pass: e.target.value })}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm" />

              <input type="text" placeholder='From address (blank = same as username)' value={mailForm.from}
                onChange={(e) => setMailForm({ ...mailForm, from: e.target.value })}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm" />
            </div>

            <h3 className="font-semibold mb-1">App URL</h3>
            <p className="text-xs text-gray-400 mb-2">
              Where confirmation links point. Must be the address users reach the app on —
              a link to <span className="text-gray-300">localhost</span> is useless in someone's inbox.
            </p>
            <input type="text" placeholder="https://chat.yourdomain.com" value={mailForm.appBaseUrl}
              onChange={(e) => setMailForm({ ...mailForm, appBaseUrl: e.target.value })}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm mb-4" />

            <div className="bg-gray-900 rounded p-2 mb-4">
              <p className="text-[11px] text-gray-400">
                Gmail: host <span className="text-teal-300">smtp.gmail.com</span>, port 587, and an
                <span className="text-gray-300"> App Password</span> (not your normal password —
                needs 2-step verification enabled on the Google account).
              </p>
            </div>

            <div className="flex justify-between gap-2">
              <button onClick={testMail} disabled={mailTesting}
                className="bg-teal-600 hover:bg-teal-700 disabled:opacity-60 text-white px-4 py-2 rounded text-sm">
                {mailTesting ? 'Testing…' : 'Test connection'}
              </button>
              <div className="flex gap-2">
                <button onClick={() => setShowMail(false)}
                  className="bg-gray-600 hover:bg-gray-500 text-white px-4 py-2 rounded text-sm">Close</button>
                <button onClick={saveMail}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm">Save Email Settings</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Plans & Billing Modal */}
      {/* API Access: keys that let external systems chat as a chosen user */}
      {showApiKeys && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 p-6 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-xl font-bold">🔌 API Access</h2>
              <button onClick={() => setShowApiKeys(false)} className="text-gray-400 hover:text-white text-2xl leading-none">×</button>
            </div>
            <p className="text-xs text-gray-400 mb-4">
              An API key lets an external system (website, WhatsApp, app) chat with the bot without logging in.
              The request runs <b>as the chosen user</b> — inheriting their AI model, prompt, tools, and plan limits.
            </p>

            {apiKeysMsg && <div className="mb-3 text-sm text-red-400">{apiKeysMsg}</div>}

            {/* The freshly created key, shown once */}
            {freshApiKey && (
              <div className="mb-4 p-3 rounded-lg border border-green-600 bg-green-900/20">
                <p className="text-xs text-green-300 mb-1">✅ Key created — copy it now, it will NOT be shown again:</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-sm text-white bg-gray-900 px-2 py-1.5 rounded break-all">{freshApiKey}</code>
                  <button type="button"
                    onClick={() => { navigator.clipboard?.writeText(freshApiKey); }}
                    className="text-xs bg-gray-700 hover:bg-gray-600 text-white px-2 py-1.5 rounded whitespace-nowrap">Copy</button>
                </div>
              </div>
            )}

            {/* Create a new key */}
            <form onSubmit={createApiKey} className="mb-5 space-y-2 border border-gray-700 rounded-lg p-3">
              <div className="flex flex-wrap gap-2">
                <input
                  type="text" placeholder="Key name (e.g. Website Bot)" value={newApiKey.name}
                  onChange={(e) => setNewApiKey({ ...newApiKey, name: e.target.value })}
                  className="flex-1 min-w-[160px] px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm" required
                />
                <select
                  value={newApiKey.userId}
                  onChange={(e) => setNewApiKey({ ...newApiKey, userId: e.target.value })}
                  className="flex-1 min-w-[160px] px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm" required
                >
                  <option value="">— acts as which user? —</option>
                  {apiKeyUsers.map(u => <option key={u.id} value={u.id}>{u.name} ({u.email})</option>)}
                </select>
                <input
                  type="number" min="1" max="600" placeholder="req/min" value={newApiKey.rateLimitPerMin}
                  onChange={(e) => setNewApiKey({ ...newApiKey, rateLimitPerMin: e.target.value })}
                  className="w-24 px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm" title="Rate limit per minute"
                />
                <button type="submit" className="bg-rose-600 hover:bg-rose-700 text-white px-4 py-2 rounded text-sm">Create Key</button>
              </div>
              <p className="text-xs text-gray-500">Rate limit caps requests per minute — protects against a leaked key running up the AI bill.</p>
            </form>

            {/* Existing keys */}
            {apiKeysList.length === 0 ? (
              <p className="text-sm text-gray-500">No API keys yet.</p>
            ) : (
              <div className="space-y-2">
                {apiKeysList.map(k => (
                  <div key={k.id} className="flex items-center justify-between border border-gray-700 rounded-lg p-2.5">
                    <div className="min-w-0">
                      <div className="text-sm text-white font-medium truncate">
                        {k.name} {!k.isActive && <span className="text-xs text-red-400 ml-1">(revoked)</span>}
                      </div>
                      <div className="text-xs text-gray-500 truncate">
                        <code>{k.keyPrefix}…</code> · acts as {k.userName} · {k.rateLimitPerMin}/min
                        {k.lastUsedAt ? ` · last used ${new Date(k.lastUsedAt).toLocaleDateString()}` : ' · never used'}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-2 whitespace-nowrap">
                      <button type="button" onClick={() => toggleApiKey(k)}
                        className={`text-xs px-2 py-1 rounded ${k.isActive ? 'bg-yellow-700 hover:bg-yellow-600' : 'bg-green-700 hover:bg-green-600'} text-white`}>
                        {k.isActive ? 'Revoke' : 'Enable'}
                      </button>
                      <button type="button" onClick={() => deleteApiKey(k)}
                        className="text-xs text-red-400 hover:text-red-300">Delete</button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Quick usage hint */}
            <div className="mt-5 p-3 rounded-lg bg-gray-900/60 border border-gray-700">
              <p className="text-xs text-gray-400 mb-1">How to call it:</p>
              <pre className="text-xs text-gray-300 overflow-x-auto whitespace-pre-wrap break-all">{`POST /api/v1/chat
Authorization: Bearer <your key>
{ "message": "aaj ki meeting kya hai?" }`}</pre>
            </div>
          </div>
        </div>
      )}

      {showBilling && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 p-6 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-xl font-bold">💳 Plans & Billing</h2>
              <button onClick={() => setShowBilling(false)} className="text-gray-400 hover:text-white text-2xl leading-none">×</button>
            </div>
            <p className="text-sm text-gray-400 mb-4">Define subscription plans and connect Razorpay for online payments.</p>

            {billingMsg && (
              <div className="bg-gray-700 text-sm text-white p-2 rounded mb-3">{billingMsg}</div>
            )}

            {/* Plans */}
            <h3 className="font-semibold mb-2">Subscription Plans</h3>
            <div className="space-y-2 mb-3">
              {plansList.length === 0 && (
                <p className="text-sm text-gray-500">No plans yet — add your first plan below.</p>
              )}
              {plansList.map(p => (
                <div key={p._id} className={`flex items-center justify-between bg-gray-750 rounded-lg px-3 py-2 ${!p.isActive ? 'opacity-50' : ''}`}>
                  <div>
                    <span className="font-medium text-white">{p.name}</span>
                    <span className="text-sm text-gray-400 ml-2">
                      ₹{(p.pricePaise / 100).toLocaleString('en-IN')}/month — {p.monthlyMessages > 0 ? `${p.monthlyMessages.toLocaleString()} AI messages` : 'unlimited messages'}
                      {p.dailyMessages > 0 && `, max ${p.dailyMessages.toLocaleString()}/day`}
                    </span>
                  </div>
                  <div className="flex items-center gap-4">
                    {/* Which tiers include the paid extras. Editable here so a
                        pricing change doesn't require rebuilding the plan. */}
                    {[
                      { field: 'mcpEnabled', label: 'MCP' },
                      { field: 'connectorsEnabled', label: 'Connectors & Sheets' },
                      { field: 'imagesEnabled', label: 'Images' },
                      { field: 'searchEnabled', label: 'Web search' }
                    ].map(f => (
                      <label key={f.field} className="flex items-center gap-1 text-xs text-gray-300 cursor-pointer whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={p[f.field] !== false}
                          onChange={(e) => updatePlanFlag(p, f.field, e.target.checked)}
                          className="accent-teal-500"
                        />
                        {f.label}
                      </label>
                    ))}
                    <button
                      onClick={() => togglePlan(p)}
                      className={`px-3 py-1 rounded text-xs ${p.isActive ? 'bg-gray-600 hover:bg-red-600 text-white' : 'bg-green-600 hover:bg-green-700 text-white'}`}
                    >
                      {p.isActive ? 'Deactivate' : 'Reactivate'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <form onSubmit={addPlan} className="mb-6 space-y-3 border border-gray-700 rounded-lg p-3">
              <div className="flex flex-wrap gap-2">
                <input
                  type="text" placeholder="Plan name (e.g. Basic)" value={newPlan.name}
                  onChange={(e) => setNewPlan({ ...newPlan, name: e.target.value })}
                  className="flex-1 min-w-[140px] px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm" required
                />
                <input
                  type="number" placeholder="₹ / month" min="0" value={newPlan.priceINR}
                  onChange={(e) => setNewPlan({ ...newPlan, priceINR: e.target.value })}
                  className="w-28 px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm" required
                />
                <input
                  type="number" placeholder="Msgs/month (0=∞)" min="0" value={newPlan.monthlyMessages}
                  onChange={(e) => setNewPlan({ ...newPlan, monthlyMessages: e.target.value })}
                  className="w-40 px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm" required
                />
                <input
                  type="number" placeholder="Msgs/day (0=none)" min="0" value={newPlan.dailyMessages}
                  onChange={(e) => setNewPlan({ ...newPlan, dailyMessages: e.target.value })}
                  className="w-40 px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm"
                />
              </div>
              <p className="text-xs text-gray-500 mb-3">
                A daily cap stops one customer burning the whole month's budget in a day.
                Leave it 0 for no daily limit. When either cap is hit, the user is offered
                usage credits.
              </p>

              <div>
                <label className="block text-xs text-gray-400 mb-1">
                  Allowed AI models <span className="text-gray-500">(none ticked = all allowed)</span>
                </label>
                <div className="flex flex-wrap gap-x-4 gap-y-1">
                  {['claude', 'openai', 'gemini', 'groq', 'deepseek', 'openrouter', 'ollama'].map((p) => (
                    <label key={p} className="flex items-center gap-1 text-sm text-gray-300 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newPlan.allowedProviders.includes(p)}
                        onChange={(e) => setNewPlan({
                          ...newPlan,
                          allowedProviders: e.target.checked
                            ? [...newPlan.allowedProviders, p]
                            : newPlan.allowedProviders.filter(x => x !== p)
                        })}
                        className="accent-teal-500"
                      />
                      {p}
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newPlan.mcpEnabled}
                      onChange={(e) => setNewPlan({ ...newPlan, mcpEnabled: e.target.checked })}
                      className="accent-teal-500"
                    />
                    MCP servers (remote tool endpoints)
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newPlan.connectorsEnabled}
                      onChange={(e) => setNewPlan({ ...newPlan, connectorsEnabled: e.target.checked })}
                      className="accent-teal-500"
                    />
                    Connectors &amp; task sheets (Docs / Sheets / Gmail / Calendar)
                  </label>
                </div>
                <button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm">Add Plan</button>
              </div>
            </form>

            {/* Razorpay credentials */}
            <h3 className="font-semibold mb-1">Razorpay Connection</h3>
            <p className="text-xs text-gray-400 mb-2">
              Keys from <span className="text-gray-300">Razorpay Dashboard → Settings → API Keys</span>. Secrets are stored encrypted.
            </p>
            <div className="space-y-2 mb-2">
              <input
                type="text" placeholder="Key ID (rzp_test_... / rzp_live_...)" value={rzpForm.keyId}
                onChange={(e) => setRzpForm({ ...rzpForm, keyId: e.target.value })}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm"
              />
              <input
                type="password"
                placeholder={rzpStatus.hasSecret ? 'Key Secret — saved ✅ (type to replace)' : 'Key Secret'}
                value={rzpForm.keySecret}
                onChange={(e) => setRzpForm({ ...rzpForm, keySecret: e.target.value })}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm"
              />
              <input
                type="password"
                placeholder={rzpStatus.hasWebhookSecret ? 'Webhook Secret — saved ✅ (type to replace)' : 'Webhook Secret'}
                value={rzpForm.webhookSecret}
                onChange={(e) => setRzpForm({ ...rzpForm, webhookSecret: e.target.value })}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm"
              />
            </div>
            <div className="bg-gray-900 rounded p-2 mb-3">
              <p className="text-[11px] text-gray-400">
                Webhook setup (Razorpay Dashboard → Settings → Webhooks): URL =
                <span className="text-teal-300"> {API_BASE_URL}/billing/webhook</span>, event = <span className="text-gray-300">payment_link.paid</span>.
                Set a secret there and paste the same secret above.
              </p>
            </div>

            {/* Web search — lets the assistant answer questions about now */}
            <h3 className="font-semibold mb-1">Web Search</h3>
            <p className="text-xs text-gray-400 mb-2">
              Without this the assistant replies "I don't have access to real-time information"
              to anything about today's news, prices or scores. Enable it per plan after saving a key.
            </p>
            <div className="flex gap-2 mb-2">
              <select
                value={searchForm.provider}
                onChange={(e) => setSearchForm({ ...searchForm, provider: e.target.value })}
                className="px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm"
              >
                {searchProviders.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
              </select>
              <input
                type="password" autoComplete="new-password"
                placeholder={searchHasKey ? 'API key — saved ✅ (type to replace)' : 'API key'}
                value={searchForm.apiKey}
                onChange={(e) => setSearchForm({ ...searchForm, apiKey: e.target.value })}
                className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm"
              />
            </div>
            <div className="bg-gray-900 rounded p-2 mb-2">
              <p className="text-[11px] text-gray-400">
                Free tiers: <span className="text-teal-300">Brave</span> ~2,000 queries/month ·
                <span className="text-teal-300"> Tavily</span> ~1,000 · <span className="text-teal-300">Serper</span> 2,500 one-time.
                Switching provider needs that provider's own key.
              </p>
            </div>
            <div className="flex justify-between gap-2 mb-6">
              <button onClick={testSearch} disabled={searchTesting}
                className="bg-teal-600 hover:bg-teal-700 disabled:opacity-60 text-white px-4 py-2 rounded text-sm">
                {searchTesting ? 'Testing…' : 'Test search'}
              </button>
              <button onClick={saveSearch}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm">
                Save Search Settings
              </button>
            </div>

            {/* Prepaid credit pricing */}
            <h3 className="font-semibold mb-1">Credit Top-ups</h3>
            <p className="text-xs text-gray-400 mb-2">
              Price of one extra message. Users who run out mid-month can buy credit at this
              rate. Leave <span className="text-gray-300">0</span> to switch top-ups off.
            </p>
            <div className="flex items-center gap-3 mb-3">
              <div className="flex items-center gap-2">
                <span className="text-gray-400 text-sm">₹</span>
                <input
                  type="number" min="0" step="0.01"
                  placeholder="0.00"
                  value={rzpForm.creditPriceINR}
                  onChange={(e) => setRzpForm({ ...rzpForm, creditPriceINR: e.target.value })}
                  className="w-32 px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm"
                />
                <span className="text-gray-400 text-sm">per message</span>
              </div>
              {Number(rzpForm.creditPriceINR) > 0 && (
                <span className="text-xs text-teal-300">
                  ₹100 buys {Math.floor(10000 / Math.round(Number(rzpForm.creditPriceINR) * 100)).toLocaleString('en-IN')} messages
                </span>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowBilling(false)} className="bg-gray-600 hover:bg-gray-500 text-white px-4 py-2 rounded text-sm">Close</button>
              <button onClick={saveRazorpay} className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded text-sm">Save Razorpay Settings</button>
            </div>
          </div>
        </div>
      )}

      {/* Shared AI API Keys Modal */}
      {showAIKeys && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 p-6 rounded-lg w-[450px] max-h-[600px] overflow-y-auto">
            <h2 className="text-xl font-bold mb-1">Shared AI API Keys</h2>
            <p className="text-xs text-gray-400 mb-4">
              These keys are shared by all users. Assign a provider to each user from their Settings.
              Ollama (local) needs no key — it is configured on the server via OLLAMA_BASE_URL.
            </p>

            {aiKeysMessage && (
              <div className="bg-blue-500/20 border border-blue-500/40 text-blue-300 text-sm p-2 rounded mb-3">
                {aiKeysMessage}
              </div>
            )}

            <form onSubmit={saveAIKeys}>
              <div className="space-y-4">
                {[
                  { id: 'claude', label: 'Claude (Anthropic)' },
                  { id: 'openai', label: 'ChatGPT (OpenAI)' },
                  { id: 'gemini', label: 'Gemini (Google)' },
                  { id: 'groq', label: 'Groq' },
                  { id: 'deepseek', label: 'DeepSeek' },
                  { id: 'openrouter', label: 'OpenRouter' },
                  { id: 'elevenlabs', label: 'ElevenLabs (Voice / TTS)' }
                ].map(({ id, label }) => (
                  <div key={id}>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      {label}
                      {aiKeysStatus[id]?.set ? (
                        <span className="ml-2 text-xs text-green-400">
                          ✓ saved ({aiKeysStatus[id].masked})
                        </span>
                      ) : (
                        <span className="ml-2 text-xs text-gray-500">not set</span>
                      )}
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="password"
                        value={aiKeyInputs[id]}
                        onChange={(e) => setAiKeyInputs(prev => ({ ...prev, [id]: e.target.value }))}
                        placeholder={aiKeysStatus[id]?.set ? 'Enter new key to replace' : 'Paste API key'}
                        autoComplete="new-password"
                        className="flex-1 p-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-500"
                      />
                      {aiKeysStatus[id]?.set && (
                        <button
                          type="button"
                          onClick={() => clearAIKey(id)}
                          className="px-3 py-2 bg-red-600/20 text-red-400 border border-red-600/40 rounded hover:bg-red-600/30 text-sm"
                          title={`Clear ${label} key`}
                        >
                          Clear
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex justify-end space-x-2 mt-5">
                <button
                  type="button"
                  onClick={() => setShowAIKeys(false)}
                  className="px-4 py-2 bg-gray-600 rounded"
                >
                  Close
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
                >
                  Save Keys
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Shared Connectors & Task Sheets — configure once, assign to users */}
      {showShared && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 p-6 rounded-lg w-[640px] max-h-[85vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-1">Shared Connectors & Task Sheets</h2>
            <p className="text-xs text-gray-400 mb-4">
              Set up a company sheet or connector <b>once</b> here, then just tick it for each
              user in their Settings — they only supply their own FMS ID. Beats re-typing the
              same URL and token inside every employee.
              <br />
              <b>Test connection</b> checks the <i>saved</i> row — after editing a URL or token, Save first.
            </p>

            {sharedMessage && (
              <div className="bg-blue-500/20 border border-blue-500/40 text-blue-300 text-sm p-2 rounded mb-3">
                {sharedMessage}
              </div>
            )}

            <form onSubmit={saveShared}>
              {/* ---- Task sheets (FMS) ---- */}
              <div className="flex justify-between items-center mb-2">
                <h3 className="font-semibold">Task Sheets (FMS)</h3>
                <button type="button" onClick={addSharedSheet}
                  className="px-3 py-1 bg-teal-600 hover:bg-teal-700 text-white rounded text-sm">+ Add sheet</button>
              </div>
              {sharedSheets.length === 0 && (
                <p className="text-sm text-gray-500 py-2">No shared task sheets yet.</p>
              )}
              <div className="space-y-3 mb-6">
                {sharedSheets.map((s, i) => (
                  <div key={s.id || `ns-${i}`} className="bg-gray-750 p-3 rounded-lg border border-gray-700/60 space-y-2">
                    <div className="flex gap-2">
                      <input type="text" value={s.name} onChange={(e) => changeShared(setSharedSheets, i, 'name', e.target.value)}
                        placeholder="Name (e.g. Company FMS)"
                        className="flex-1 p-2 bg-gray-700 border border-gray-600 rounded text-white font-medium text-sm" />
                      <input type="text" value={s.keywords} onChange={(e) => changeShared(setSharedSheets, i, 'keywords', e.target.value)}
                        placeholder="keywords: daily task, fms"
                        className="flex-1 p-2 bg-gray-700 border border-gray-600 rounded text-white text-sm" />
                      <button type="button" onClick={() => removeShared(setSharedSheets, i)}
                        className="px-2 text-red-500 hover:text-red-400 text-sm">Remove</button>
                    </div>
                    <input type="url" value={s.url} onChange={(e) => changeShared(setSharedSheets, i, 'url', e.target.value)}
                      placeholder="Apps Script URL (https://script.google.com/.../exec)" pattern="https://.*"
                      className="w-full p-2 bg-gray-700 border border-gray-600 rounded text-white text-sm" />
                    <div className="grid grid-cols-5 gap-2">
                      <input type="text" value={s.sheetName} onChange={(e) => changeShared(setSharedSheets, i, 'sheetName', e.target.value)}
                        placeholder="Tab" className="p-2 bg-gray-700 border border-gray-600 rounded text-white text-sm" />
                      <input type="text" value={s.matchCol} onChange={(e) => changeShared(setSharedSheets, i, 'matchCol', e.target.value.toUpperCase())}
                        placeholder="Match" maxLength={3} className="p-2 bg-gray-700 border border-gray-600 rounded text-white text-sm" />
                      <input type="text" value={s.showCols} onChange={(e) => changeShared(setSharedSheets, i, 'showCols', e.target.value.toUpperCase())}
                        placeholder="Show" className="p-2 bg-gray-700 border border-gray-600 rounded text-white text-sm" />
                      <input type="text" value={s.statusCol} onChange={(e) => changeShared(setSharedSheets, i, 'statusCol', e.target.value.toUpperCase())}
                        placeholder="Status" maxLength={3} className="p-2 bg-gray-700 border border-gray-600 rounded text-white text-sm" />
                      <input type="number" min="1" max="100" value={s.maxRows} onChange={(e) => changeShared(setSharedSheets, i, 'maxRows', e.target.value)}
                        placeholder="Rows" className="p-2 bg-gray-700 border border-gray-600 rounded text-white text-sm" />
                    </div>
                    <input type="password" value={s.token} onChange={(e) => changeShared(setSharedSheets, i, 'token', e.target.value)}
                      placeholder={s.hasToken ? 'Token saved — type a new one to replace' : 'Secret token (optional)'}
                      autoComplete="new-password"
                      className="w-full p-2 bg-gray-700 border border-gray-600 rounded text-white text-sm" />
                    <ConnectionTest kind="fms" id={s.id} state={connTests[`fms:${s.id}`]} onTest={runConnectionTest} />
                  </div>
                ))}
              </div>

              {/* ---- Connectors ---- */}
              <div className="flex justify-between items-center mb-2">
                <h3 className="font-semibold">Connectors</h3>
                <button type="button" onClick={addSharedConn}
                  className="px-3 py-1 bg-teal-600 hover:bg-teal-700 text-white rounded text-sm">+ Add connector</button>
              </div>
              {sharedConns.length === 0 && (
                <p className="text-sm text-gray-500 py-2">No shared connectors yet.</p>
              )}
              <div className="space-y-3">
                {sharedConns.map((c, i) => (
                  <div key={c.id || `nc-${i}`} className="bg-gray-750 p-3 rounded-lg border border-gray-700/60 space-y-2">
                    <div className="flex gap-2">
                      <select value={c.type} onChange={(e) => changeShared(setSharedConns, i, 'type', e.target.value)}
                        className="p-2 bg-gray-700 border border-gray-600 rounded text-white text-sm">
                        <option value="docs">Google Docs</option>
                        <option value="gmail">Gmail</option>
                        <option value="calendar">Calendar + Meet</option>
                        <option value="sheets">Google Sheets</option>
                      </select>
                      <input type="text" value={c.name} onChange={(e) => changeShared(setSharedConns, i, 'name', e.target.value)}
                        placeholder="Name" className="flex-1 p-2 bg-gray-700 border border-gray-600 rounded text-white font-medium text-sm" />
                      <button type="button" onClick={() => removeShared(setSharedConns, i)}
                        className="px-2 text-red-500 hover:text-red-400 text-sm">Remove</button>
                    </div>
                    <input type="text" value={c.keywords} onChange={(e) => changeShared(setSharedConns, i, 'keywords', e.target.value)}
                      placeholder="keywords (comma-separated)"
                      className="w-full p-2 bg-gray-700 border border-gray-600 rounded text-white text-sm" />
                    <input type="url" value={c.url} onChange={(e) => changeShared(setSharedConns, i, 'url', e.target.value)}
                      placeholder="Apps Script URL" pattern="https://.*"
                      className="w-full p-2 bg-gray-700 border border-gray-600 rounded text-white text-sm" />
                    <input type="password" value={c.token} onChange={(e) => changeShared(setSharedConns, i, 'token', e.target.value)}
                      placeholder={c.hasToken ? 'Token saved — type a new one to replace' : 'Secret token (optional)'}
                      autoComplete="new-password"
                      className="w-full p-2 bg-gray-700 border border-gray-600 rounded text-white text-sm" />
                    <ConnectionTest kind="connector" id={c.id} state={connTests[`connector:${c.id}`]} onTest={runConnectionTest} />
                  </div>
                ))}
              </div>

              <div className="flex gap-2 mt-6">
                <button type="submit" className="flex-1 bg-teal-600 hover:bg-teal-700 text-white py-2 rounded-lg">Save</button>
                <button type="button" onClick={() => setShowShared(false)}
                  className="flex-1 bg-gray-600 hover:bg-gray-700 text-white py-2 rounded-lg">Close</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Shared MCP Servers Modal */}
      {showMCP && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 p-6 rounded-lg w-[520px] max-h-[600px] overflow-y-auto">
            <div className="flex justify-between items-center mb-1">
              <h2 className="text-xl font-bold">Shared MCP Servers</h2>
              <button
                type="button"
                onClick={addMCPRow}
                className="px-3 py-1 bg-teal-600 text-white rounded hover:bg-teal-700 text-sm"
              >
                + Add
              </button>
            </div>
            <p className="text-xs text-gray-400 mb-4">
              Remote MCP servers (HTTPS URL) give the AI extra tools — GitHub, Linear, Notion, etc.
              Works with the Claude provider. Assign servers to users from their Settings.
            </p>

            {mcpMessage && (
              <div className="bg-blue-500/20 border border-blue-500/40 text-blue-300 text-sm p-2 rounded mb-3">
                {mcpMessage}
              </div>
            )}

            <form onSubmit={saveMCP}>
              <div className="space-y-4">
                {mcpList.length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-4">
                    No MCP servers yet. Click "+ Add" to create one.
                  </p>
                )}
                {mcpList.map((server, index) => (
                  <div key={server.id || `new-${index}`} className="bg-gray-750 p-3 rounded-lg border border-gray-700/60">
                    <div className="flex gap-2 mb-2">
                      <input
                        type="text"
                        value={server.name}
                        onChange={(e) => changeMCPRow(index, 'name', e.target.value)}
                        placeholder="Name (e.g. github)"
                        className="flex-1 p-2 bg-gray-700 border border-gray-600 rounded text-white font-medium"
                      />
                      <button
                        type="button"
                        onClick={() => removeMCPRow(index)}
                        className="p-2 text-red-500 hover:text-red-400"
                        title="Remove server"
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2M10 11v6M14 11v6"/>
                        </svg>
                      </button>
                    </div>
                    <input
                      type="url"
                      value={server.url}
                      onChange={(e) => changeMCPRow(index, 'url', e.target.value)}
                      placeholder="Server URL (https://mcp.example.com/sse)"
                      className="w-full p-2 bg-gray-700 border border-gray-600 rounded text-white mb-2"
                      pattern="https://.*"
                    />
                    <input
                      type="password"
                      value={server.token}
                      onChange={(e) => changeMCPRow(index, 'token', e.target.value)}
                      placeholder={server.hasToken ? 'Auth token saved — enter new to replace' : 'Auth token (optional)'}
                      autoComplete="new-password"
                      className="w-full p-2 bg-gray-700 border border-gray-600 rounded text-white"
                    />
                    {server.hasToken && (
                      <p className="text-[11px] text-green-400 mt-1">✓ token saved (encrypted)</p>
                    )}
                    <div className="mt-2">
                      <ConnectionTest kind="mcp" id={server.id} state={connTests[`mcp:${server.id}`]} onTest={runConnectionTest} />
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex justify-end space-x-2 mt-5">
                <button
                  type="button"
                  onClick={() => setShowMCP(false)}
                  className="px-4 py-2 bg-gray-600 rounded"
                >
                  Close
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700"
                >
                  Save Servers
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!pendingConfirm}
        title={pendingConfirm?.title}
        message={pendingConfirm?.message}
        confirmLabel={pendingConfirm?.confirmLabel}
        onConfirm={() => pendingConfirm?.onConfirm()}
        onCancel={() => setPendingConfirm(null)}
      />
    </div>
  );
};

export default AdminPanel;
